import { useEffect, useRef, useState, useMemo } from "react";
import { docAmounts } from "../../utils/docAmounts";
import { AmountCell, numericColumn } from "../../components/tableCells";
import ListTotalsFooter from "../../components/ListTotalsFooter";
import CancelledReceivedDialog from "../../components/CancelledReceivedDialog";
import {
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  InputLabel,
  FormControl,
  MenuItem,
  Button,
  TextField,
  Box,
  Tooltip,
  IconButton,
  Snackbar,
  LinearProgress,
  Alert,
  CircularProgress,
  Checkbox,
  Chip,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Layout from "../../layout/Layout";
import {
  fetchUnitsAPI,
  fetchOrdersAPI,
  cancelOrderAPI,
  cancelOrderOTPAPI,
  fetchOrderDateHistoryAPI,
  settleAndConvertCreditInvoiceAPI,
  invoiceConversionHistoryAPI,
  paymentSettlementsAPI,
  uploadToVercelAPI,
  invoiceAccessAPI,
  GetDeliveryNotePdfAPI,
} from "../../config";
import { formatAED, formatNumber } from "../../utils/format";
import ListFilterBar, { emptyListFilters, inferPeriod } from "../../components/ListFilterBar";
import { useNavigate } from "react-router-dom";
import { usePersistedState } from "../../utils/usePersistedState";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { useLocation } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import dayjs from "dayjs";
import { formatLocalDateTime } from "../../utils/datetime";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { apiData } from "../../services/api_service";
import PaymentAttachmentUpload from "../../components/PaymentAttachmentUpload";
import AttachmentLink from "../../components/AttachmentLink";
import ReturnExchangeDialog from "./ReturnExchangeDialog";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import moment from "moment";
import CancelIcon from "@mui/icons-material/Cancel";
import { WarningAmber } from "@mui/icons-material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { FileDownload } from "@mui/icons-material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { PDFDocument } from "pdf-lib";

// Canonical, human-readable status for an order row, combining the stored
// order_status (Completed/Cancelled) with the derived return_status. Kept in one
// place so the Status column and any status-driven UI stay in sync.
const ORDER_STATUS_OPTIONS = [
  "Completed",
  "Partially Returned",
  "Returned",
  "Cancelled",
];

// Payment attachments are stored as a plain URL (no separate filename
// column) — derive something readable from the last path segment so the
// list shows a name instead of a bare icon/link.
const attachmentFileName = (url) => {
  try {
    const path = new URL(url).pathname;
    const last = decodeURIComponent(path.split("/").pop() || "");
    return last || "Attachment";
  } catch {
    return "Attachment";
  }
};

const ORDER_STATUS_STYLES = {
  Completed: { color: "#2e7d32", backgroundColor: "#e8f5e9", borderColor: "#c3e6cb" },
  "Partially Returned": { color: "#b26a00", backgroundColor: "#fff4e5", borderColor: "#ffd8a8" },
  Returned: { color: "#1565c0", backgroundColor: "#e7f0fb", borderColor: "#bcd6f5" },
  Cancelled: { color: "#c62828", backgroundColor: "#fdecea", borderColor: "#f5c6c2" },
};

const getOrderStatusLabel = (order) => {
  if (order.order_status === "Cancelled") return "Cancelled";
  const rs = order.return_status || "none";
  if (rs === "full") return "Returned";
  if (rs === "partial") return "Partially Returned";
  return order.order_status || "Completed";
};

const FOC_STATUS_STYLES = {
  Yes: { color: "#ffffff", backgroundColor: "#0c54a0", borderColor: "#b8d7ff" },
  Partial: { color: "#0c54a0", backgroundColor: "#e8f1ff", borderColor: "#b8d7ff" },
  No: { color: "#0c54a0", backgroundColor: "#e8f1ff", borderColor: "#b8d7ff" },
};

const getFocStatusLabel = (order) => {
  const items = Array.isArray(order?.items) ? order.items : [];
  if (!items.length) return Number(order?.has_foc) === 1 ? "Yes" : "No";

  const focCount = items.filter((item) => Number(item?.is_foc) === 1 || item?.isFoc).length;
  if (focCount === 0) return "No";
  return focCount === items.length ? "Yes" : "Partial";
};

function FocStatusCell({ row }) {
  const label = getFocStatusLabel(row.original);
  const style = FOC_STATUS_STYLES[label] || FOC_STATUS_STYLES.No;
  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Chip
        label={label}
        size="small"
        sx={{
          height: 24,
          fontWeight: 700,
          fontSize: 11,
          color: style.color,
          backgroundColor: style.backgroundColor,
          border: `1px solid ${style.borderColor}`,
        }}
      />
    </Box>
  );
};

// Status pill for the dedicated Status column.
function OrderStatusCell({ row }) {
  const label = getOrderStatusLabel(row.original);
  const style = ORDER_STATUS_STYLES[label] || ORDER_STATUS_STYLES.Completed;
  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Chip
        label={label}
        size="small"
        sx={{
          height: 26,
          fontWeight: 700,
          fontSize: 11,
          color: style.color,
          backgroundColor: style.backgroundColor,
          border: `1px solid ${style.borderColor}`,
        }}
      />
    </Box>
  );
}

// Actions cell for the orders grid — controls only. The order's state now lives
// in its own Status column. This builds the list of actions that apply to the
// row, then always surfaces the first (highest-priority) one inline as the
// primary button so the Actions column is never just a lone kebab; the rest
// drop into the compact "more" (kebab) menu. Handlers/permissions are passed in
// so this stays purely presentational.
// Payment status shown in the list: Paid, Partially Paid, Unpaid, Overdue or
// Cancelled. "Pending" stays the stored status (and an "all outstanding" filter).
const getPaymentStatusLabel = (payment, orderStatus) => {
  if (String(orderStatus || "").toLowerCase() === "cancelled") return "Cancelled";
  const stored = String(payment?.payment_status || "");
  if (["paid", "completed", "success"].includes(stored.toLowerCase())) return "Paid";
  const balance = Number(payment?.balance_due);
  if (!(balance > 0)) return stored || "-";
  if (payment?.due_date && dayjs(payment.due_date).isBefore(dayjs(), "day")) return "Overdue";
  if (Number(payment?.paid_so_far) > 0) return "Partially Paid";
  return "Unpaid";
};

// Whole days past the due date for a row the list labels "Overdue" (0 otherwise).
const getOverdueDays = (payment, orderStatus) => {
  if (getPaymentStatusLabel(payment, orderStatus) !== "Overdue" || !payment?.due_date) return 0;
  return Math.max(0, dayjs().startOf("day").diff(dayjs(payment.due_date).startOf("day"), "day"));
};
const overdueText = (days) => `${days} ${days === 1 ? "day" : "days"} overdue`;

function OrderActionsCell({ row, onCancel, onReturn, onEdit, onHistory, canEdit }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const closeMenu = () => setAnchorEl(null);

  const { order_status } = row.original;
  const returnStatus = row.original.return_status || "none";
  // Credit Note viewing disabled — credit notes are no longer generated on
  // return/exchange (stock still reverts to the source store).
  // const creditNotes = row.original.credit_notes || [];
  // const creditNoteUrl = creditNotes.find((c) => c.pdf_url)?.pdf_url;
  const isCancelled = order_status === "Cancelled";
  const isFullyReturned = returnStatus === "full";

  // A cancelled or fully-returned order can't be cancelled or returned again.
  const canCancel = !isCancelled && !isFullyReturned;
  const canReturn = !isCancelled && !isFullyReturned;
  // Editing is blocked for cancelled orders and users without edit permission.
  const editDisabled = isCancelled || !canEdit;

  // Actions in priority order. actions[0] renders inline as the primary button;
  // everything after it goes into the "more" menu. Edit is always included last
  // so cancelled / fully-returned orders (which have no Cancel/Return) still
  // have a fallback primary action and the column never looks empty.
  const actions = [];
  if (canCancel) {
    actions.push({
      key: "cancel",
      label: "Cancel",
      icon: <CancelIcon fontSize="small" />,
      variant: "contained",
      color: "error",
      onClick: () => onCancel(row.original),
    });
  }
  // if (canReturn) {
  //   actions.push({
  //     key: "return",
  //     label:
  //       returnStatus === "partial"
  //         ? "Return remaining / Exchange"
  //         : "Return / Exchange",
  //     icon: <AssignmentReturnIcon fontSize="small" color="primary" />,
  //     onClick: () => onReturn(row.original),
  //   });
  // }
  // Credit Note action disabled — credit notes are no longer generated.
  // if (creditNoteUrl) {
  //   actions.push({
  //     key: "credit-note",
  //     label: "Credit Note",
  //     icon: <PictureAsPdfIcon fontSize="small" sx={{ color: "#b00020" }} />,
  //     href: creditNoteUrl,
  //   });
  // }
  actions.push({
    key: "edit",
    label: "Edit invoice",
    icon: (
      <EditIcon fontSize="small" color={editDisabled ? "disabled" : "primary"} />
    ),
    disabled: editDisabled,
    // Cancelled orders and missing edit permission both disable the button,
    // but only the permission case gets an explanatory tooltip.
    disabledTitle: !canEdit && !isCancelled ? "Access restricted" : "",
    onClick: () => onEdit(row.original.order_id),
  });
  // Only shown once a Credit->Invoice conversion actually happened (Settle
  // Payment, or an edit that changed Payment Type) — nothing to show for an
  // invoice that was always plain Immediate/Paid.
  const hasConversionHistory = Boolean(row.original.has_conversion_history);
  if (hasConversionHistory) {
    actions.push({
      key: "history",
      label: "Invoice History",
      icon: <HistoryIcon fontSize="small" color="action" />,
      onClick: () => onHistory(row.original.order_id),
    });
  }

  const [primary, ...secondary] = actions;

  // Fixed width so every row's primary button (Cancel / Credit Note / Edit) is
  // exactly the same length regardless of its label.
  const primaryBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: 12,
    borderRadius: 1.5,
    width: 138,
    whiteSpace: "nowrap",
    boxShadow: "none",
    "&:hover": { boxShadow: "0 2px 6px rgba(0,0,0,0.18)" },
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.75,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {primary && (
        <Tooltip title={primary.disabledTitle || ""}>
          <span>
            <Button
              size="small"
              variant={primary.variant || "outlined"}
              color={primary.color || "primary"}
              disableElevation
              disabled={primary.disabled}
              startIcon={primary.icon}
              {...(primary.href
                ? { href: primary.href, target: "_blank", rel: "noopener noreferrer" }
                : { onClick: primary.onClick })}
              sx={primaryBtnSx}
            >
              {primary.label}
            </Button>
          </span>
        </Tooltip>
      )}

      {secondary.length > 0 && (
        <>
          <Tooltip title="More actions" arrow>
            <IconButton
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                border: "1px solid #d9dde3",
                borderRadius: 1.5,
                width: 32,
                height: 32,
                color: "#5f6b7a",
                "&:hover": { backgroundColor: "#eef1f5" },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={closeMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { borderRadius: 2, minWidth: 210 } }}
          >
            {secondary.map((action) =>
              action.href ? (
                <MenuItem
                  key={action.key}
                  component="a"
                  href={action.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMenu}
                >
                  <ListItemIcon>{action.icon}</ListItemIcon>
                  <ListItemText>{action.label}</ListItemText>
                </MenuItem>
              ) : (
                <MenuItem
                  key={action.key}
                  disabled={action.disabled}
                  onClick={() => {
                    closeMenu();
                    action.onClick();
                  }}
                >
                  <ListItemIcon>{action.icon}</ListItemIcon>
                  <ListItemText>{action.label}</ListItemText>
                </MenuItem>
              )
            )}
          </Menu>
        </>
      )}
    </Box>
  );
}

const parseInitialFilters = (state) => {
  if (state?.filters && Array.isArray(state.filters) && state.filters.length > 0) {
    return state.filters;
  }
  if (!state) return [];

  const newFilters = [];
  const { fromDate, toDate, storeId, customerId, salesPersonId, cardType, period, reportTitle, paymentStatus, payment_status, paymentMethod } = state;
  const todayStr = dayjs().format("YYYY-MM-DD");

  if (cardType === "today_sales" || period === "daily" || reportTitle === "Today's Sales Report") {
    newFilters.push({ id: "order_date_from", value: todayStr });
    newFilters.push({ id: "order_date_to", value: todayStr });
  } else {
    if (fromDate) {
      newFilters.push({ id: "order_date_from", value: fromDate });
    }
    if (toDate) {
      newFilters.push({ id: "order_date_to", value: toDate });
    }
  }

  const statusVal = (paymentStatus || payment_status || "").toString().toLowerCase();
  if (statusVal === "paid") {
    newFilters.push({ id: "payment_status", value: "Paid" });
  } else if (statusVal === "pending") {
    newFilters.push({ id: "payment_status", value: "Pending" });
  } else if (["partial", "unpaid", "received", "fullypaid", "zero"].includes(statusVal)) {
    newFilters.push({ id: "payment_status", value: statusVal });
  }

  // Opened from a dashboard card: the dashboard counts only real (finished) invoices, so the list
  // does too. The "Cancelled" card is the exception — it asks for cancelled invoices.
  if (statusVal === "cancelled") {
    newFilters.push({ id: "payment_status", value: "Cancelled" });
  } else if (reportTitle) {
    newFilters.push({ id: "real_invoices", value: "1" });
  }

  if (paymentMethod) {
    // Exact label (the dashboard splits "Cash" from "Cash + Card").
    newFilters.push({ id: "payment_method_exact", value: paymentMethod });
  }

  if (storeId && parseInt(storeId) > 0) {
    newFilters.push({ id: "store_id", value: storeId });
  }
  if (customerId && parseInt(customerId) > 0) {
    newFilters.push({ id: "customer_id", value: customerId });
  }
  if (salesPersonId && parseInt(salesPersonId) > 0) {
    newFilters.push({ id: "created_by", value: salesPersonId });
  }

  return newFilters;
};

const getOrderGrandTotal = (order) => {
  const storedGrand = parseFloat(order?.grand_total || 0);
  if (storedGrand > 0) return storedGrand;

  const subtotal =
    order?.items?.reduce((acc, item) => acc + parseFloat(item.total_price || 0), 0) || 0;
  const itemDiscount =
    order?.items?.reduce((acc, item) => acc + parseFloat(item.discount_amount || 0), 0) || 0;
  const topDiscount = parseFloat(order?.discount_amount || 0);
  const shipping = parseFloat(order?.shipping_amount || 0);
  const netSubtotal = Math.max(0, subtotal - itemDiscount - topDiscount);

  return netSubtotal + netSubtotal * 0.05 + shipping;
};

const SettlePaymentDialog = ({
  open,
  onClose,
  isReadOnly,
  paymentId,
  initialMethod,
  initialTransactionId,
  initialDescription,
  onSubmit,
  submitting,
  // Conversion preview shown inline (not a separate dialog/step) — settling
  // a Credit Invoice's payment and converting it to a Tax Invoice + emailing
  // it is one decision, made once, right here. These are the same facts the
  // old separate confirm dialog showed, just folded into this one.
  orderNumber,
  customerName,
  customerEmail,
  invoiceDate,
  amount,
  balanceDue,
  storeName,
  previewUrl,
}) => {
  // Each line is one payment method's worth of this settlement — e.g. one
  // line for "Cash: 500", another for "Card: 300" on the same visit. At
  // least one line is always present; its amount defaults to the full
  // balance due so a normal single-method full settlement needs no extra
  // clicks, same as before.
  const makeDefaultLines = () => [
    {
      method: initialMethod,
      transactionId: initialTransactionId,
      amount:
        balanceDue !== undefined && balanceDue !== null && balanceDue > 0
          ? String(Math.round(balanceDue * 100) / 100)
          : "",
    },
  ];
  const [lines, setLines] = useState(makeDefaultLines);
  const [description, setDescription] = useState(initialDescription);

  useEffect(() => {
    if (!open) return;
    setLines(makeDefaultLines());
    setDescription(initialDescription);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialMethod, initialTransactionId, initialDescription, balanceDue]);

  // Itemised history of everything already recorded against this payment —
  // who took it, when, how much, by which method — so staff can see the
  // full trail before adding another entry, not just the balance number.
  const [settlementHistory, setSettlementHistory] = useState([]);
  const [settlementHistoryLoading, setSettlementHistoryLoading] = useState(false);

  useEffect(() => {
    if (!open || !paymentId) {
      setSettlementHistory([]);
      return;
    }
    let cancelled = false;
    setSettlementHistoryLoading(true);
    apiData("get", `${paymentSettlementsAPI}/${paymentId}/settlements`)
      .then((res) => {
        if (!cancelled) setSettlementHistory(res?.data || []);
      })
      .catch((err) => {
        console.error("Failed to load payment settlement history:", err);
        if (!cancelled) setSettlementHistory([]);
      })
      .finally(() => {
        if (!cancelled) setSettlementHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, paymentId]);

  const updateLine = (index, patch) => {
    setLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    );
  };

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      { method: "", transactionId: "", amount: "", attachmentUrl: "", attachmentName: "", uploading: false },
    ]);
  };

  const removeLine = (index) => {
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const enteredTotal = lines.reduce(
    (sum, line) => sum + (parseFloat(line.amount) || 0),
    0,
  );
  const dueAmount = typeof balanceDue === "number" ? balanceDue : 0;
  const remainingAfter = Math.max(0, Math.round((dueAmount - enteredTotal) * 100) / 100);
  const overpaid = enteredTotal - dueAmount > 0.01;
  const willFullySettle = !overpaid && remainingAfter <= 0.01 && enteredTotal > 0;
  const linesWithAmountMissingMethod = lines.some(
    (l) => parseFloat(l.amount) > 0 && !l.method,
  );
  const canSubmit =
    enteredTotal > 0 && !overpaid && !linesWithAmountMissingMethod;

  const handleSubmit = () => {
    onSubmit({
      lines: lines.map((l) => ({
        method: l.method,
        transactionId: String(l.transactionId || "").trim(),
        amount: l.amount,
        attachmentUrl: l.attachmentUrl || "",
        attachmentName: l.attachmentName || "",
      })),
      description,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      disableEscapeKeyDown={submitting}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        {isReadOnly ? "Payment Details" : "Settle Payment & Convert to Invoice"}
      </DialogTitle>

      <DialogContent sx={{ position: "relative" }}>
        {!isReadOnly && (
          <Box
            sx={{
              mb: 1.5,
              border: "1px solid #e5e7eb",
              borderRadius: 1,
              overflow: "hidden",
              backgroundColor: "#fff",
            }}
          >
            {[
              ["Invoice No.", orderNumber || "-"],
              ["Customer", customerName || "-"],
              ["Email To", customerEmail || "-"],
              ["Invoice Date", invoiceDate || "-"],
              ["Amount", amount || "-"],
              ["Store", storeName || "-"],
            ].map(([label, value], index) => (
              <Box
                key={label}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "120px 1fr" },
                  gap: { xs: 0.25, sm: 2 },
                  px: 2,
                  py: 1,
                  borderTop: index === 0 ? "none" : "1px solid #eef0f3",
                }}
              >
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ color: "#111827", fontWeight: 600 }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {!isReadOnly && (
          <Box
            sx={{
              mb: 1,
              p: 1.5,
              borderRadius: 1,
              display: "flex",
              gap: 1,
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
            }}
          >
            <ReceiptLongOutlinedIcon sx={{ color: "#1976d2", mt: 0.2 }} fontSize="small" />
            <Typography variant="body2" color="text.secondary">
              Recording this payment will regenerate this invoice as a{" "}
              <strong>TAX INVOICE</strong> and email it to the customer.
              {previewUrl && (
                <>
                  {" "}
                  <Button
                    component="a"
                    href={previewUrl}
                    target="_blank"
                    rel="noreferrer"
                    size="small"
                    startIcon={<OpenInNewIcon fontSize="small" />}
                    sx={{ fontWeight: 700, textTransform: "none", verticalAlign: "baseline" }}
                  >
                    Preview Credit Invoice
                  </Button>
                </>
              )}
            </Typography>
          </Box>
        )}

        {!isReadOnly && typeof balanceDue === "number" && (
          <Box
            sx={{
              mb: 1,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: overpaid ? "#fdecea" : "#eef6ff",
              border: `1px solid ${overpaid ? "#f5c2c0" : "#cfe3fb"}`,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Balance Due: {formatAED(dueAmount)}
            </Typography>
            <Typography variant="caption" color={overpaid ? "error" : "text.secondary"}>
              {overpaid
                ? `Entered amount is ${formatAED(enteredTotal - dueAmount)} more than the balance due.`
                : willFullySettle
                  ? "This fully settles the balance — the invoice will convert to a Tax Invoice."
                  : enteredTotal > 0
                    ? `${formatAED(remainingAfter)} will remain due after this payment. The invoice stays a Credit Invoice.`
                    : "Enter the amount(s) being paid now."}
            </Typography>
          </Box>
        )}

        {(settlementHistoryLoading || settlementHistory.length > 0) && (
          <Box
            sx={{
              mb: 1,
              border: "1px solid #e2e8f0",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontWeight: 700,
                color: "#64748b",
                px: 1.5,
                py: 0.75,
                backgroundColor: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              Payment History
            </Typography>
            {settlementHistoryLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                <CircularProgress size={18} />
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell>Attachment</TableCell>
                    <TableCell>Recorded By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settlementHistory.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>
                        {formatLocalDateTime(s.created_at)}
                      </TableCell>
                      <TableCell>{formatAED(s.amount)}</TableCell>
                      <TableCell>{s.payment_method || "-"}</TableCell>
                      <TableCell>{s.transaction_id || "-"}</TableCell>
                      <TableCell>
                        <AttachmentLink url={s.attachment_url} name={s.attachment_name} showEmpty />
                      </TableCell>
                      <TableCell>{s.created_by_name || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}

        {!isReadOnly && lines.length > 0 && (
          <Typography variant="caption" sx={{ display: "block", fontWeight: 700, color: "#64748b", mt: 0.5 }}>
            {settlementHistory.length > 0 ? "Add New Payment" : "Payment"}
          </Typography>
        )}

        {lines.map((line, index) => (
          <Box key={index}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              mt: 1,
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            <FormControl fullWidth margin="normal" sx={{ minWidth: 160 }}>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={line.method}
                label="Payment Method"
                onChange={(event) => updateLine(index, { method: event.target.value })}
                disabled={isReadOnly}
              >
                <MenuItem value="1">Cash</MenuItem>
                <MenuItem value="2">Card</MenuItem>
                <MenuItem value="3">Cheque</MenuItem>
                <MenuItem value="4" style={{ display: "none" }}>
                  Bank Transfer (Legacy)
                </MenuItem>
                <MenuItem value="5">Bank Transfer</MenuItem>
                <MenuItem value="6">Online Payment</MenuItem>
                <MenuItem value="7">Bill to Company</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Amount"
              type="number"
              margin="normal"
              value={line.amount}
              onChange={(event) => updateLine(index, { amount: event.target.value })}
              inputProps={{ autoComplete: "off", min: 0, step: "0.01" }}
              disabled={isReadOnly}
              sx={{ minWidth: 140 }}
            />

            <TextField
              label="Transaction ID"
              fullWidth
              margin="normal"
              value={line.transactionId}
              onChange={(event) => updateLine(index, { transactionId: event.currentTarget.value })}
              inputProps={{ autoComplete: "off" }}
              disabled={isReadOnly}
            />

            {!isReadOnly && lines.length > 1 && (
              <IconButton
                aria-label="Remove payment line"
                onClick={() => removeLine(index)}
                sx={{ mt: 2 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          {!isReadOnly && (
            <Box sx={{ mt: 0.5, maxWidth: 420 }}>
              <PaymentAttachmentUpload
                height={40}
                label="Attach payment proof (optional)"
                url={line.attachmentUrl}
                name={line.attachmentName}
                uploading={line.uploading}
                uploadApi={uploadToVercelAPI}
                onUploadStart={() => updateLine(index, { uploading: true })}
                onUploadSuccess={({ url, name }) =>
                  updateLine(index, { attachmentUrl: url, attachmentName: name, uploading: false })
                }
                onUploadError={() => updateLine(index, { uploading: false })}
                onRemove={() => updateLine(index, { attachmentUrl: "", attachmentName: "" })}
              />
            </Box>
          )}
          </Box>
        ))}

        {!isReadOnly && (
          <Button size="small" onClick={addLine} sx={{ mt: 0.5, textTransform: "none" }}>
            + Add another payment method
          </Button>
        )}

        <TextField
          id="settle-payment-description"
          label="Description"
          fullWidth
          margin="normal"
          value={description}
          onChange={(event) => setDescription(event.currentTarget.value)}
          inputProps={{ autoComplete: "off" }}
          disabled={isReadOnly}
        />

        {submitting && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.72)",
              zIndex: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">
                {willFullySettle ? "Converting & emailing invoice..." : "Recording payment..."}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      {!isReadOnly && (
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitting || !canSubmit}
            startIcon={willFullySettle ? <EmailOutlinedIcon fontSize="small" /> : undefined}
          >
            {willFullySettle ? "Convert & Email" : "Record Partial Payment"}
          </LoadingButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

// Cancel-invoice OTP: valid for 15 minutes; the session is kept in sessionStorage.
const OTP_VALID_SECONDS = 15 * 60;
const OTP_STORAGE_KEY = "invoice-cancel-otp";
const formatOtpTime = (secs) =>
  `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;


// One tick a second, in the component that calls it only — never in the list.
const useSecondsLeft = (expiresAt) => {
  const calc = () => Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
  const [left, setLeft] = useState(calc);
  useEffect(() => {
    setLeft(calc());
    const id = setInterval(() => setLeft(calc()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt]);
  return left;
};

// "12:30" that counts down by itself.
const OtpTime = ({ expiresAt }) => <>{formatOtpTime(useSecondsLeft(expiresAt))}</>;

// Countdown + progress bar inside the OTP dialog; tells the parent once on expiry.
const OtpTimerBlock = ({ expiresAt, onExpiredChange }) => {
  const left = useSecondsLeft(expiresAt);
  const expired = left <= 0;
  useEffect(() => {
    onExpiredChange(expired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expired]);
  return (
    <Box sx={{ mt: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.75 }}>
        <Typography sx={{ fontSize: 12.5, color: expired ? "#B42318" : "#64748B", fontWeight: expired ? 700 : 400 }}>
          {expired ? "This OTP has expired" : "OTP expires in"}
        </Typography>
        {!expired && (
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: left < 60 ? "#B42318" : "#0F172A" }}>
            {formatOtpTime(left)}
          </Typography>
        )}
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, (left / OTP_VALID_SECONDS) * 100))}
        color={left < 60 ? "error" : "primary"}
        sx={{ height: 6, borderRadius: 3, bgcolor: "#E3E6EA" }}
      />
    </Box>
  );
};

// The "Cancellation pending" reminder. The whole bar is clickable.
const OtpReminderBar = ({ session, onOpen, onForget }) => {
  const left = useSecondsLeft(session.expiresAt);
  if (left <= 0) return null;
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      sx={{
        position: "fixed",
        left: 24,
        bottom: 24,
        zIndex: 20000,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        bgcolor: "#0F172A",
        color: "#fff",
        borderRadius: "12px",
        px: 2,
        py: 1.25,
        cursor: "pointer",
        boxShadow: "0 8px 24px rgba(15,23,42,0.28)",
        "&:hover": { bgcolor: "#1E293B" },
      }}
    >
      <LockOutlinedIcon fontSize="small" />
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
          Cancellation pending — {session.order_number}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#CBD5E1" }}>OTP valid for {formatOtpTime(left)}</Typography>
      </Box>
      <Button
        size="small"
        variant="contained"
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#fff", color: "#0F172A", "&:hover": { bgcolor: "#E2E8F0" }, borderRadius: "8px" }}
      >
        Enter OTP
      </Button>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onForget();
        }}
        aria-label="Forget this OTP"
        sx={{ color: "#94A3B8" }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

function OrderMasterList() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const location = useLocation();
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalReceived, setTotalReceived] = useState(0);
  const [totalBalanceDue, setTotalBalanceDue] = useState(0);
  // Cancelled invoices are left out of the totals above; this is the money that
  // had already been received against them, shown on its own.
  const [cancelledSummary, setCancelledSummary] = useState({ count: 0, received: 0 });
  const [cancelledOpen, setCancelledOpen] = useState(false);
  const [activeCount, setActiveCount] = useState(null);
  const [orderList, setOrderList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = usePersistedState("invoice-list:pageIndex", 0);
  const [pageSize, setPageSize] = usePersistedState("invoice-list:pageSize", 100);
  const [filters, setFilters] = usePersistedState("invoice-list:filters", () => parseInitialFilters(location.state));
  // Free-text search from the filter bar (server-side "globalSearch").
  const [globalSearch, setGlobalSearch] = usePersistedState("invoice-list:search", "");
  const BAR_FILTER_IDS = ["order_date_from", "order_date_to", "store_id", "customer_id", "created_by", "product_ids"];
  // The filter bar reads its values from the same `filters` the table fetches
  // with, so dashboard drill-downs and the bar always agree.
  const barValue = (() => {
    const get = (id) => filters.find((x) => x.id === id)?.value;
    const fromDate = get("order_date_from") || "";
    const toDate = get("order_date_to") || "";
    const n = (v) => (Number(v) > 0 ? Number(v) : 0);
    return {
      ...emptyListFilters,
      period: inferPeriod(fromDate, toDate),
      fromDate,
      toDate,
      storeId: n(get("store_id")),
      customerId: n(get("customer_id")),
      salesPersonId: n(get("created_by")),
      productIds: String(get("product_ids") || "").split(",").map(Number).filter((x) => x > 0),
      search: globalSearch,
    };
  })();
  const applyBar = (next) => {
    setFilters((prev) => {
      const kept = prev.filter((x) => !BAR_FILTER_IDS.includes(x.id));
      const add = [];
      if (next.fromDate) add.push({ id: "order_date_from", value: next.fromDate });
      if (next.toDate) add.push({ id: "order_date_to", value: next.toDate });
      if (Number(next.storeId) > 0) add.push({ id: "store_id", value: String(next.storeId) });
      if (Number(next.customerId) > 0) add.push({ id: "customer_id", value: String(next.customerId) });
      if (Number(next.salesPersonId) > 0) add.push({ id: "created_by", value: String(next.salesPersonId) });
      if ((next.productIds || []).length) add.push({ id: "product_ids", value: next.productIds.join(",") });
      return [...kept, ...add];
    });
    setGlobalSearch(next.search || "");
    setPageIndex(0);
  };
  const [sorting, setSorting] = usePersistedState("invoice-list:sorting", []);
  const [unitsList, setUnitsList] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [cancelError, setCancelError] = useState("");
  const [otpExpired, setOtpExpired] = useState(false);
  // The pending OTP (order + expiry) — survives closing the dialog and a refresh.
  const [otpSession, setOtpSession] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(OTP_STORAGE_KEY) || "null");
      return saved && saved.expiresAt > Date.now() ? saved : null;
    } catch (e) {
      return null;
    }
  });
  // Not a ticking value (the countdown lives in small child components); this is
  // read when the page renders anyway, e.g. on any click or keystroke.
  const otpActive = Boolean(otpSession && otpSession.expiresAt > Date.now());
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [settleDialogOpen, setSettleDialogOpen] = useState(false);
  const [settleMethod, setSettleMethod] = useState("");
  const [settleDescription, setSettleDescription] = useState("");
  const [settleTransactionId, setSettleTransactionId] = useState("");
  const [settleBalanceDue, setSettleBalanceDue] = useState(null);
  const [settlingPayment, setSettlingPayment] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [orderDateHistory, setOrderDateHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  // Settling a Credit Invoice's payment and converting it to a Tax Invoice
  // (regenerate PDF + email the customer) is ONE dialog / ONE atomic action —
  // nothing is saved unless "Convert & Email" is actually clicked. See
  // SettlePaymentDialog and handleSubmitPayment below. selectedOrderRow feeds
  // the customer/amount/email preview shown inside that same dialog, plus
  // the separate per-order conversion-history dialog (audit trail of who/when).
  const [selectedOrderRow, setSelectedOrderRow] = useState(null);
  const [conversionHistoryDialogOpen, setConversionHistoryDialogOpen] = useState(false);
  const [conversionHistory, setConversionHistory] = useState([]);
  const [loadingConversionHistory, setLoadingConversionHistory] = useState(false);
  // Order selected for the Return / Exchange dialog.
  const [returnOrder, setReturnOrder] = useState(null);

  // Order whose full item list / payment list is being shown in a dialog.
  // (Table rows show only a one-line summary so every row stays the same height.)
  const [itemsDialogOrder, setItemsDialogOrder] = useState(null);
  const [paymentsDialogOrder, setPaymentsDialogOrder] = useState(null);
  // { order, loading, rows } for the "Received" breakdown dialog, else null
  const [receivedDialog, setReceivedDialog] = useState(null);

  // Everything that makes up an invoice's Received amount: each settlement
  // line (advances carried over from a CDN / Proforma, Settle Payment
  // entries, split payments). An invoice paid in one go at creation has no
  // settlement lines, so its payment row itself is shown instead.
  const openReceivedDialog = async (order) => {
    setReceivedDialog({ order, loading: true, rows: [] });
    const rows = [];
    for (const p of order.payments || []) {
      const res = await apiData(
        "get",
        `${paymentSettlementsAPI}/${p.payment_id}/settlements`
      ).catch(() => null);
      const lines = res?.success && Array.isArray(res.data) ? res.data : [];
      if (lines.length) {
        rows.push(...lines);
      } else if (String(p.payment_status || "").toLowerCase() === "paid") {
        rows.push({
          id: `p-${p.payment_id}`,
          amount: p.amount,
          payment_method: p.payment_method,
          transaction_id: p.transaction_ref || p.transaction_id,
          created_at: p.created_at,
          created_by_name: order.created_by_name || null,
          paidAtInvoice: true,
        });
      }
    }
    setReceivedDialog((prev) => (prev ? { ...prev, loading: false, rows } : prev));
  };

  // ----- Compose multiple invoices (order receipts) into one PDF -----
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeMode, setMergeMode] = useState("month"); // "month" | "date" | "manual"
  const [mergeFromMonth, setMergeFromMonth] = useState(dayjs().startOf("month"));
  const [mergeToMonth, setMergeToMonth] = useState(dayjs().startOf("month"));
  const [mergeFromDate, setMergeFromDate] = useState(dayjs().startOf("month"));
  const [mergeToDate, setMergeToDate] = useState(dayjs());
  const [mergeOrders, setMergeOrders] = useState([]);
  const [mergeSelectedIds, setMergeSelectedIds] = useState([]);
  const [mergeSearch, setMergeSearch] = useState("");
  const [mergeFetching, setMergeFetching] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeMessage, setMergeMessage] = useState("");

  let tempRefs = useRef(false);
  const inputRefs = useRef([]);

  // Current user's invoice permissions (default open until loaded).
  const [invoiceAccess, setInvoiceAccess] = useState({
    can_create_invoice: true,
    can_edit_invoice: true,
  });

  const handleEditStore = (id) => {
    // Plain client-side navigation — Orders.jsx already refetches the
    // invoice via a useEffect keyed on the :id param, so no full page
    // reload is needed (that used to cause a visible flash/reload here).
    navigate(`/invoice/edit/${id}`);
  };

  useEffect(() => {
    if (tempRefs.current === false) {
      const initial = parseInitialFilters(location.state);
      if (initial.length === 0) {
        fetchOrders(1);
      }
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiData("get", invoiceAccessAPI);
        if (res.success && res.data) setInvoiceAccess(res.data);
      } catch (e) {
        console.error("Failed to fetch invoice access:", e);
      }
    })();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    const payload = {
      pageIndex: pageIndex,
      pageSize: pageSize,
      sorting: sorting.length > 0 ? JSON.stringify(sorting[0]) : null,
      search: filters.length > 0 ? JSON.stringify(filters) : null,
      globalSearch: globalSearch || undefined,
    };
    try {
      const response = await apiData("get", fetchOrdersAPI, payload);
      setOrderList(response.data.result);
      setTotalCount(response.data.totalCount);
      setTotalAmount(parseFloat(response.data.totalAmount || 0));
      setTotalReceived(parseFloat(response.data.totalReceived || 0));
      setTotalBalanceDue(parseFloat(response.data.totalBalanceDue || 0));
      setActiveCount(response.data.activeCount ?? null);
      setCancelledSummary({
        count: Number(response.data.cancelledCount || 0),
        received: parseFloat(response.data.cancelledReceived || 0),
      });
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDateHistory = async (orderId) => {
    try {
      setLoadingHistory(true);
      setSelectedOrderId(orderId);

      const res = await apiData(
        "get",
        `${fetchOrderDateHistoryAPI}/${orderId}`,
      );

      if (res?.success) {
        setOrderDateHistory(res.data);
        setOpenHistoryDialog(true);
      }
    } catch (error) {
      console.error("Failed to load order date history", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [pageIndex, pageSize, filters, sorting, globalSearch]);

  useEffect(() => {
    const newFilters = parseInitialFilters(location.state);
    if (newFilters.length > 0) {
      setFilters(newFilters);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchUnits = async () => {
      const data = {
        intParentId: 88,
      };

      try {
        const result = await apiData("get", fetchUnitsAPI, data);
        if (result.success) {
          setUnitsList(result.data);
        }
      } catch (error) {
        console.error("Failed to fetch units:", error);
      }
    };

    fetchUnits();
  }, []);

  // Cancel-invoice OTP session. Kept in state AND sessionStorage so closing the
  // dialog (or refreshing the page) doesn't lose it: the OTP stays valid for
  // 15 minutes, and "Enter OTP" brings the dialog back without sending another.
  const handleOpenDialog = (order) => {
    setCancelError("");
    setSelectedOrder(order);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedOrder(null);
    setCancelError("");
  };

  const focusOtpBox = (i = 0) => setTimeout(() => inputRefs.current[i]?.focus(), 150);

  const startOtpSession = (order) => {
    const session = {
      order_id: order.order_id,
      order_number: order.order_number,
      customer_name: order.customer_name || "",
      expiresAt: Date.now() + OTP_VALID_SECONDS * 1000,
    };
    setOtpSession(session);
    setOtpExpired(false);
    try {
      sessionStorage.setItem(OTP_STORAGE_KEY, JSON.stringify(session));
    } catch (e) {
      /* storage unavailable — the session just won't survive a refresh */
    }
  };

  const clearOtpSession = () => {
    setOtpSession(null);
    setOtp("");
    setOtpError("");
    try {
      sessionStorage.removeItem(OTP_STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
  };

  const reopenOtpDialog = () => {
    setOpenDialog(false);
    setOtpError("");
    setOtpDialogOpen(true);
    focusOtpBox(0);
  };

  const handleSendOtp = async (orderArg) => {
    const order = orderArg && orderArg.order_id ? orderArg : selectedOrder || otpSession;
    try {
      if (!order) return;
      setSendingOtp(true);
      setCancelError("");

      const payload = {
        order_id: order.order_id,
        order_number: order.order_number,
        created_user: user.emp_id,
        user_id: user.emp_id,
        created_name: user.username,
      };

      const result = await apiData("post", cancelOrderOTPAPI, payload);

      if (result.success) {
        startOtpSession(order);
        setOtp("");
        setOtpError("");
        setOpenDialog(false);
        setOtpDialogOpen(true);
        focusOtpBox(0);
      } else {
        const msg = result.message || result.msg || "Failed to send the OTP.";
        if (otpDialogOpen) setOtpError(msg);
        else setCancelError(msg);
      }
    } catch (error) {
      console.error("Send OTP failed:", error);
      const msg = "Couldn't send the OTP. Please try again.";
      if (otpDialogOpen) setOtpError(msg);
      else setCancelError(msg);
    } finally {
      setSendingOtp(false);
    }
  };

  const handleResendOtp = () => handleSendOtp(otpSession);

  const handleSubmitOtp = async () => {
    if (!otpSession || otp.length !== 4) return;
    try {
      setVerifyingOtp(true);
      setOtpError("");
      const payload = {
        order_id: otpSession.order_id,
        order_number: otpSession.order_number,
        created_user: user.emp_id,
        user_id: user.emp_id,
        cancel_otp: otp,
      };

      const result = await apiData("post", cancelOrderAPI, payload);

      if (result.success) {
        setSuccessMessage(`Invoice ${otpSession.order_number} cancelled. The stock has been returned.`);
        setOtpDialogOpen(false);
        clearOtpSession();
        fetchOrders();
      } else {
        setOtpError(result.message || result.msg || "That OTP is invalid or has expired.");
        setOtp("");
        focusOtpBox(0);
      }
    } catch (error) {
      console.error("OTP verification failed:", error);
      setOtpError("Couldn't verify the OTP. Please try again.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Settling a Credit Invoice's payment and converting it to a Tax Invoice
  // (regenerate PDF, email the customer, log the conversion) is ONE atomic
  // backend call — nothing is written to tbl_order_payments unless this
  // whole thing succeeds, so cancelling the dialog leaves the invoice
  // untouched (still Pending, still Credit).
  const handleSubmitPayment = async (paymentId, paymentData) => {
    if (!paymentId || !selectedOrderId) return;

    // paymentData.lines: one or more { method, transactionId, amount } rows —
    // lets one visit be split across methods (part cash, part card), and/or
    // cover only part of the balance due (rest recorded later as its own
    // settlement). Each becomes its own settlement entry server-side.
    const payload = {
      payment_id: paymentId,
      settlements: (paymentData.lines || [])
        .filter((l) => parseFloat(l.amount) > 0)
        .map((l) => ({
          amount: parseFloat(l.amount) || 0,
          payment_method: ID_TO_PAYMENT_METHOD_LABEL[l.method] || l.method,
          transaction_id: l.transactionId,
          description: paymentData.description,
          attachment_url: l.attachmentUrl || null,
          attachment_name: l.attachmentName || null,
        })),
    };

    try {
      setSettlingPayment(true);
      const result = await apiData(
        "post",
        `${settleAndConvertCreditInvoiceAPI}/${selectedOrderId}/settle-and-convert`,
        payload,
      );
      if (!result?.success) {
        alert(result?.message || "Failed to settle payment.");
        return;
      }

      const message = result.message || "Payment settled and invoice converted.";
      setSuccessMessage(/[.!?]$/.test(message) ? message : `${message}.`);
      setSettleDialogOpen(false);
      fetchOrders();
    } catch (error) {
      console.error("Failed to settle and convert credit invoice:", error);
      alert("Error occurred while settling payment.");
    } finally {
      setSettlingPayment(false);
    }
  };

  const handleOpenConversionHistory = async (orderId) => {
    setConversionHistoryDialogOpen(true);
    setLoadingConversionHistory(true);
    try {
      const result = await apiData(
        "get",
        `${invoiceConversionHistoryAPI}/${orderId}/conversion-history`,
      );
      setConversionHistory(result?.data || []);
    } catch (error) {
      console.error("Failed to load conversion history:", error);
      setConversionHistory([]);
    } finally {
      setLoadingConversionHistory(false);
    }
  };

  // Reverse of paymentOptions below (label stored on the payment -> id the
  // settle dialog's Select expects). Keep both lists in sync.
  const PAYMENT_METHOD_LABEL_TO_ID = {
    Cash: "1",
    Card: "2",
    Cheque: "3",
    "Bank Transfer": "5",
    "Online Payment": "6",
    "Bill to Company": "7",
  };
  const ID_TO_PAYMENT_METHOD_LABEL = Object.fromEntries(
    Object.entries(PAYMENT_METHOD_LABEL_TO_ID).map(([label, id]) => [id, label]),
  );

  const loadPaymentIntoDialog = (p) => {
    setSettleMethod(PAYMENT_METHOD_LABEL_TO_ID[p.payment_method] || "");

    setSettleTransactionId(p.transaction_id || "");
    setSettleDescription(p.description || "");
    // Prior settlements already applied to this payment (if it was
    // previously part-paid) — the dialog defaults its first line to
    // whatever's still outstanding, not the full original amount.
    setSettleBalanceDue(
      p.balance_due !== undefined && p.balance_due !== null
        ? parseFloat(p.balance_due)
        : null,
    );
  };

  const getPaymentTypeLabel = (type) => {
    if (type === 2) return "Immediate";
    if (type === 1) return "Credit";
    return "N/A";
  };

  // Renders just the FIRST entry of a list (items/payments) as one clamped line,
  // plus a "+N" badge when there are more. Every row therefore takes exactly one
  // line for this column instead of growing to fit every item — clicking anywhere
  // opens the full list in a dialog (see itemsDialogOrder / paymentsDialogOrder).
  // `data-lmggt-handled` tells the global TableCellExpander to leave this cell
  // alone so its own generic "reveal clipped text" click doesn't also fire.
  const renderFirstPlusMore = (list, renderLine, onExpand) => {
    if (!list || list.length === 0) return "NA";
    return (
      <Box
        data-lmggt-handled="true"
        onClick={onExpand}
        title={list.length > 1 ? `Click to view all ${list.length}` : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          overflow: "hidden",
          cursor: "pointer",
        }}
      >
        <Box
          component="span"
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {renderLine(list[0])}
        </Box>
        {list.length > 1 && (
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              fontSize: 11,
              fontWeight: 700,
              color: "#1976d2",
              backgroundColor: "#e3f2fd",
              borderRadius: "10px",
              padding: "1px 7px",
              lineHeight: 1.6,
            }}
          >
            +{list.length - 1}
          </Box>
        )}
      </Box>
    );
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "order_number",
        header: "Invoice No.",
        size: 20,
        // Clicking the invoice number opens the generated invoice PDF
        // (order_receipt). Same pattern as the CDN Ref column below.
        Cell: ({ row }) => {
          const num = row.original.order_number;
          const url = row.original.order_receipt;
          if (!num) return "-";
          return url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", fontWeight: 600, textDecoration: "none" }}
            >
              {num}
            </a>
          ) : (
            num
          );
        },
      },
      {
        accessorKey: "invoice_type",
        header: "Invoice Type",
        size: 20,
        filterVariant: "select",
        // Same underlying pay.payment_type as the Payment Terms column below,
        // just relabelled for this column's purpose (2=Immediate -> "Invoice",
        // 1=Credit -> "Credit Invoice"). See getAllOrdersWithDetails in the API.
        filterSelectOptions: [
          { label: "Invoice", value: 2 },
          { label: "Credit Invoice", value: 1 },
        ],
        Cell: ({ row }) => {
          const list = row.original.payments;
          if (!list || list.length === 0) return "-";
          return list[0].payment_type === 1 ? "Credit Invoice" : "Invoice";
        },
      },
      {
        accessorKey: "proforma_number",
        header: "CDN Ref",
        size: 20,
        // Text filter — type (part of) the CDN/proforma number to match.
        // Present only for invoices created by converting a proforma. Clicking
        // the number opens that proforma's receipt PDF.
        Cell: ({ row }) => {
          const num = row.original.proforma_number;
          const url = row.original.proforma_pdf_url;
          if (!num) return "-";
          return url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", fontWeight: 600, textDecoration: "none" }}
            >
              {num}
            </a>
          ) : (
            num
          );
        },
      },
      {
        id: "foc_status",
        header: "FOC",
        size: 20,
        enableSorting: false,
        // A pure `id` column with no accessor is treated as a display-only
        // column internally, which silently drops the header's column-action
        // menu (and with it the filter icon) no matter what enableColumnFilter
        // says. Giving it an accessorFn — same label logic as the Cell below —
        // makes MRT treat it as a real data column so the filter actually
        // shows up. The returned value itself is never sent anywhere; actual
        // filtering happens server-side (manualFiltering) off filterVariant.
        accessorFn: (row) => getFocStatusLabel(row),
        enableColumnFilter: true,
        filterVariant: "select",
        filterSelectOptions: [
          { label: "Yes", value: "Yes" },
          { label: "Partial", value: "Partial" },
          { label: "No", value: "No" },
        ],
        Cell: FocStatusCell,
      },
      {
        accessorKey: "order_date",
        header: "Invoice Date",
        size: 50,
        Cell: ({ row }) => (
          <div>
            <div style={{ fontWeight: 600 }}>
              {dayjs(row.original.order_date).format("DD-MM-YYYY")}
            </div>

            <div
              style={{
                color: "#1976d2",
                fontSize: 12,
                fontWeight: 600,
                textDecoration: "underline",
                cursor: "pointer",
                marginTop: 4,
              }}
              onClick={() => fetchOrderDateHistory(row.original.order_id)}
            >
              View Date History
            </div>
          </div>
        ),
        Filter: ({ column }) => {
          const value = column.getFilterValue() || null;

          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                value={value ? dayjs(value, "YYYY-MM-DD") : null}
                inputFormat="DD-MM-YYYY"
                onChange={(newValue) => {
                  if (newValue) {
                    const formatted = dayjs(newValue).format("YYYY-MM-DD");
                    column.setFilterValue(formatted);
                  } else {
                    column.setFilterValue(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="standard"
                    placeholder="Select Date"
                    sx={{
                      "& .MuiInputBase-input": {
                        color: "#fff !important",
                      },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#fff !important",
                        opacity: 0.7,
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          {value && (
                            <IconButton
                              size="small"
                              onClick={() => column.setFilterValue(null)}
                              sx={{ color: "#fff" }}
                            >
                              ✕
                            </IconButton>
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          );
        },
      },
      {
        accessorKey: "payment_type",
        header: "Payment Terms",
        size: 50,
        filterVariant: "select",
        filterSelectOptions: [
          { label: "Credit", value: 1 },
          { label: "Immediate", value: 2 },
        ],
        Cell: ({ row }) =>
          renderFirstPlusMore(
            row.original.payments,
            (p) => getPaymentTypeLabel(p.payment_type),
            () => setPaymentsDialogOrder(row.original),
          ),
      },
      {
        accessorKey: "payment_method",
        header: "Payment Method",
        size: 50,
        filterVariant: "select",
        filterSelectOptions: [
          { label: "Cash", value: "Cash" },
          { label: "Card", value: "Card" },
          { label: "Cheque", value: "Cheque" },
          { label: "Bank Transfer", value: "Bank Transfer" },
          { label: "Online Payment", value: "Online Payment" },
          { label: "Bill to Company", value: "Bill to Company" },
        ],
        Cell: ({ row }) =>
          renderFirstPlusMore(
            row.original.payments,
            (p) => p.payment_method || "N/A",
            () => setPaymentsDialogOrder(row.original),
          ),
      },
      {
        accessorKey: "created_by_name",
        header: "Created By",
        size: 40,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "created_at",
        header: "Entry Date",
        size: 50,
        Cell: ({ cell }) =>
          cell.getValue() ? dayjs(cell.getValue()).format("DD-MM-YYYY") : "-",
      },
      {
        accessorKey: "updated_at",
        header: "Last Updated",
        size: 50,
        Cell: ({ cell }) =>
          cell.getValue() ? dayjs(cell.getValue()).format("DD-MM-YYYY") : "-",
      },
      {
        accessorKey: "order_receipt",
        header: "View Invoice",
        size: 50,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => {
          const url = cell.getValue();
          return url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: "90px",
                height: "30px",
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <RemoveRedEyeIcon color="blue" fontSize="small" />
            </a>
          ) : (
            <span>No Receipt</span>
          );
        },
      },
      {
        accessorKey: "delivery_note_url",
        header: "Delivery Note",
        size: 50,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const url = row.original.delivery_note_url;
          if (url) {
            return (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  width: "90px",
                  height: "30px",
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LocalShippingOutlinedIcon color="success" fontSize="small" />
              </a>
            );
          }
          return (
            <Tooltip title="View & Print Delivery Note PDF" arrow>
              <IconButton
                size="small"
                color="success"
                onClick={() => handleFetchDeliveryNotePdf(row.original.order_id)}
              >
                <LocalShippingOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "store_name",
        header: "Store",
        size: 50,
      },
      {
        accessorKey: "customer_name",
        header: "Company Name / Bill To Company",
        size: 50,
      },
      {
        accessorKey: "customer_phone",
        header: "Phone",
        size: 50,
        Cell: ({ cell }) => cell.getValue() || "NA",
      },
      {
        accessorKey: "customer_email",
        header: "Email",
        size: 50,
        Cell: ({ cell }) => cell.getValue() || "NA",
      },
      {
        accessorKey: "product_name",
        header: "Ordered Products",
        size: 50,
        Cell: ({ row }) =>
          renderFirstPlusMore(
            row.original.items,
            (item) => `${item.product_name} (${item.quantity} ${item.name})`,
            () => setItemsDialogOrder(row.original),
          ),
      },
      {
        accessorkey: "qty",
        header: "Qty",
        size: 50,
        Cell: ({ row }) =>
          renderFirstPlusMore(
            row.original.items,
            (item) => item.quantity,
            () => setItemsDialogOrder(row.original),
          ),
      },
      {
        accessorKey: "selling_price",
        header: "Price (Per Qty)",
        size: 50,
        Cell: ({ row }) =>
          renderFirstPlusMore(
            row.original.items,
            (item) => formatAED(item.selling_price),
            () => setItemsDialogOrder(row.original),
          ),
      },
      {
        id: "doc_subtotal",
        header: "Subtotal",
        size: 60,
        enableSorting: false,
        enableColumnFilter: false,
        ...numericColumn,
        accessorFn: (row) => docAmounts(row).subtotal,
        Cell: ({ row }) => <AmountCell value={docAmounts(row.original).subtotal} />,
      },
      {
        id: "doc_discount",
        header: "Discount",
        size: 60,
        enableSorting: false,
        enableColumnFilter: false,
        ...numericColumn,
        accessorFn: (row) => docAmounts(row).discount,
        Cell: ({ row }) => <AmountCell value={docAmounts(row.original).discount} zeroAsDash />,
      },
      {
        id: "doc_vat",
        header: "VAT",
        size: 60,
        enableSorting: false,
        enableColumnFilter: false,
        ...numericColumn,
        accessorFn: (row) => docAmounts(row).vat,
        Cell: ({ row }) => <AmountCell value={docAmounts(row.original).vat} zeroAsDash />,
      },
      {
        id: "doc_shipping",
        header: "Shipping",
        size: 60,
        enableSorting: false,
        enableColumnFilter: false,
        ...numericColumn,
        accessorFn: (row) => docAmounts(row).shipping,
        Cell: ({ row }) => <AmountCell value={docAmounts(row.original).shipping} zeroAsDash />,
      },
      {
        accessorKey: "grand_total",
        header: "Grand Total / Outstanding Amount",
        size: 50,
        Cell: ({ row }) => {
          const storedGrand = parseFloat(row.original.grand_total || 0);
          if (storedGrand > 0) return formatAED(storedGrand);

          const subtotal =
            row.original.items?.reduce(
              (acc, i) => acc + parseFloat(i.total_price || 0),
              0,
            ) || 0;
          const itemDiscount =
            row.original.items?.reduce(
              (acc, i) => acc + parseFloat(i.discount_amount || 0),
              0,
            ) || 0;
          const topDiscount = parseFloat(row.original.discount_amount || 0);
          const discount = itemDiscount + topDiscount;
          const netSubtotal = Math.max(0, subtotal - discount);
          const tax = netSubtotal * 0.05;
          const shipping = parseFloat(row.original.shipping_amount || 0);
          const grandTotal = netSubtotal + tax + shipping;
          return formatAED(grandTotal);
        },
      },
      // Received / Balance Due — what's been collected (settlements and CDN /
      // Proforma advances) and what's still owed; Balance Due matches the
      // dashboard's Pending Collections card.
      {
        id: "amount_received",
        header: "Received",
        size: 50,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const grand = parseFloat(row.original.grand_total || 0);
          const due = (row.original.payments || []).reduce(
            (s, p) => s + parseFloat(p.balance_due || 0),
            0
          );
          const received = Math.max(0, grand - due);
          if (received <= 0) return formatAED(0);
          // Click to see where the money came from.
          return (
            <Tooltip title="View payments received">
              <Link
                component="button"
                underline="hover"
                sx={{ fontSize: "inherit", fontWeight: 600 }}
                onClick={() => openReceivedDialog(row.original)}
              >
                {formatAED(received)}
              </Link>
            </Tooltip>
          );
        },
      },
      {
        id: "balance_due",
        header: "Balance Due",
        size: 50,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const due = (row.original.payments || []).reduce(
            (s, p) => s + parseFloat(p.balance_due || 0),
            0
          );
          return (
            <Typography sx={{ fontSize: "inherit", fontWeight: 700, color: due > 0 ? "#b45309" : "#16a34a" }}>
              {formatAED(due)}
            </Typography>
          );
        },
      },
      {
        accessorKey: "due_date",
        header: "Due Date",
        size: 50,
        Cell: ({ row }) =>
          renderFirstPlusMore(
            row.original.payments,
            (p) => (p.due_date ? dayjs(p.due_date).format("DD-MM-YYYY") : "N/A"),
            () => setPaymentsDialogOrder(row.original),
          ),
        Filter: ({ column }) => {
          const value = column.getFilterValue() || null;

          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                value={value ? dayjs(value, "YYYY-MM-DD") : null}
                inputFormat="DD-MM-YYYY"
                onChange={(newValue) => {
                  if (newValue) {
                    const formatted = dayjs(newValue).format("YYYY-MM-DD");
                    column.setFilterValue(formatted);
                  } else {
                    column.setFilterValue(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="standard"
                    placeholder="Select Date"
                    sx={{
                      "& .MuiInputBase-input": {
                        color: "#fff !important",
                      },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#fff !important",
                        opacity: 0.7,
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          {value && (
                            <IconButton
                              size="small"
                              onClick={() => column.setFilterValue(null)}
                              sx={{ color: "#fff" }}
                            >
                              ✕
                            </IconButton>
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          );
        },
      },
      {
        accessorKey: "payment_status",
        header: "Payment Status",
        size: 50,
        filterVariant: "select",
        filterSelectOptions: [
          { label: "Pending (all outstanding)", value: "Pending" },
          { label: "Unpaid", value: "unpaid_not_due" },
          { label: "Partially Paid", value: "partial_not_due" },
          { label: "Overdue", value: "Overdue" },
          { label: "Paid", value: "Paid" },
          { label: "Cancelled", value: "Cancelled" },
        ],
        // Shows only the FIRST payment's status (with its original click-to-edit
        // behaviour, unchanged) so the row stays one line tall. A "+N" badge opens
        // the full Payment Details dialog when there's more than one payment.
        Cell: ({ row }) => {
          const payments = row.original.payments || [];
          if (payments.length === 0) return "NA";
          const first = payments[0];
          const isCredit = first.payment_type === 1;
          // Unpaid / part-paid / overdue credit invoices get an obvious button —
          // the status text alone doesn't look clickable.
          const canSettle =
            isCredit &&
            ["Unpaid", "Partially Paid", "Overdue"].includes(
              getPaymentStatusLabel(first, row.original.order_status)
            );
          const openSettle = () => {
            setSelectedPaymentId(first.payment_id);
            setSelectedOrderId(row.original.order_id);
            setSelectedOrderRow(row.original);
            setIsReadOnly(false);
            loadPaymentIntoDialog(first);
            setSettleDialogOpen(true);
          };

          return (
            <Box
              data-lmggt-handled="true"
              sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}
            >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, overflow: "hidden" }}
            >
              <Box
                component="span"
                sx={{
                  cursor: isCredit ? "pointer" : "default",
                  color: isCredit ? "blue" : "inherit",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={
                  !isCredit
                    ? "Payment method does not allow status update"
                    : first.payment_status === "Pending"
                      ? "Click to edit payment"
                      : "Click to view payment"
                }
                onClick={() => {
                  if (!isCredit) return;
                  setSelectedPaymentId(first.payment_id);
                  setSelectedOrderId(row.original.order_id);
                  setSelectedOrderRow(row.original);
                  setIsReadOnly(first.payment_status === "Paid");

                  loadPaymentIntoDialog(first);
                  setSettleDialogOpen(true);
                }}
              >
                {getPaymentStatusLabel(first, row.original.order_status)}
                {getOverdueDays(first, row.original.order_status) > 0 && (
                  <Box component="span" sx={{ display: "block", fontSize: 11, fontWeight: 700, color: "#B42318" }}>
                    {overdueText(getOverdueDays(first, row.original.order_status))}
                  </Box>
                )}
                {first.payment_status === "Partially Paid" &&
                  first.balance_due != null && (
                    <Box component="span" sx={{ display: "block", fontSize: 11, color: "#64748b" }}>
                      {formatAED(first.balance_due)} due
                    </Box>
                  )}
              </Box>
              {payments.length > 1 && (
                <Box
                  component="span"
                  onClick={() => setPaymentsDialogOrder(row.original)}
                  title={`Click to view all ${payments.length} payments`}
                  sx={{
                    flexShrink: 0,
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#1976d2",
                    backgroundColor: "#e3f2fd",
                    borderRadius: "10px",
                    padding: "1px 7px",
                    lineHeight: 1.6,
                    cursor: "pointer",
                  }}
                >
                  +{payments.length - 1}
                </Box>
              )}
              {/* Only shown once a Credit->Invoice conversion has actually
                  happened (Settle Payment, or an edit that changed Payment
                  Type) — a plain Immediate/Paid invoice that was never
                  Credit has nothing to show history for. */}
              {Boolean(row.original.has_conversion_history) && (
                <HistoryIcon
                  fontSize="inherit"
                  titleAccess="View invoice conversion history"
                  onClick={() => handleOpenConversionHistory(row.original.order_id)}
                  sx={{
                    flexShrink: 0,
                    fontSize: 14,
                    color: "#9e9e9e",
                    cursor: "pointer",
                    "&:hover": { color: "#1976d2" },
                  }}
                />
              )}
            </Box>
              {canSettle && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={openSettle}
                  sx={{
                    textTransform: "none",
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: 1.4,
                    py: 0.1,
                    px: 1,
                    minWidth: 0,
                    whiteSpace: "nowrap",
                    borderRadius: "6px",
                  }}
                >
                  Settle Payment
                </Button>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "payment_date",
        header: "Payment Date",
        size: 50,
        Cell: ({ row }) =>
          renderFirstPlusMore(
            row.original.payments,
            (p) => {
              const d = p.payment_date || p.updated_at || p.created_at;
              return d ? dayjs(d).format("DD-MM-YYYY") : "N/A";
            },
            () => setPaymentsDialogOrder(row.original),
          ),
        Filter: ({ column }) => {
          const value = column.getFilterValue() || null;

          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                value={value ? dayjs(value, "YYYY-MM-DD") : null}
                inputFormat="DD-MM-YYYY"
                onChange={(newValue) => {
                  if (newValue) {
                    const formatted = dayjs(newValue).format("YYYY-MM-DD");
                    column.setFilterValue(formatted);
                  } else {
                    column.setFilterValue(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="standard"
                    placeholder="Select Date"
                    sx={{
                      "& .MuiInputBase-input": {
                        color: "#fff !important",
                      },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#fff !important",
                        opacity: 0.7,
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          {value && (
                            <IconButton
                              size="small"
                              onClick={() => column.setFilterValue(null)}
                              sx={{ color: "#fff" }}
                            >
                              ✕
                            </IconButton>
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          );
        },
      },
      {
        accessorKey: "credit_description",
        header: "Payment Description",
        size: 50,
        Cell: ({
          row: {
            original: { payments },
          },
        }) =>
          payments.length > 0 ? (
            <div>{payments[0].description || "N/A"}</div>
          ) : (
            <div>N/A</div>
          ),
      },
      {
        // Merged column: shows the settlement-time Transaction Id once one
        // exists, otherwise falls back to the sale-time Reference No — kept
        // as a single column since showing both separately was confusing.
        accessorKey: "transaction_id",
        header: "Transaction Id",
        size: 50,
        Cell: ({
          row: {
            original: { payments },
          },
        }) =>
          payments.length > 0 ? (
            <div>
              {payments[0].transaction_id || payments[0].transaction_ref || "-"}
            </div>
          ) : (
            <div>N/A</div>
          ),
      },
      {
        accessorKey: "payment_attachment_url",
        header: "Payment Attachment",
        enableSorting: false,
        enableColumnFilter: false,
        size: 160,
        Cell: ({
          row: {
            original: { payments },
          },
        }) => {
          const url = payments?.[0]?.payment_attachment_url;
          if (!url) return <div>-</div>;
          const fileName =
            payments?.[0]?.payment_attachment_name || attachmentFileName(url);
          return (
            <Tooltip title={`View ${fileName}`} arrow>
              <Box
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "primary.main",
                  textDecoration: "none",
                  overflow: "hidden",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <AttachFileIcon fontSize="small" sx={{ flexShrink: 0 }} />
                <Box
                  component="span"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  View File
                </Box>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        enableSorting: false,
        size: 110,
        filterVariant: "select",
        filterSelectOptions: ORDER_STATUS_OPTIONS.map((s) => ({
          label: s,
          value: s,
        })),
        Cell: ({ row }) => <OrderStatusCell row={row} />,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        size: 200,
        minSize: 200,
        Cell: ({ row }) => (
          <OrderActionsCell
            row={row}
            onCancel={handleOpenDialog}
            onReturn={setReturnOrder}
            onEdit={handleEditStore}
            onHistory={handleOpenConversionHistory}
            canEdit={invoiceAccess.can_edit_invoice}
          />
        ),
      },
    ],
    [invoiceAccess],
  );

  const handleFetchDeliveryNotePdf = async (orderId) => {
    const win = window.open("", "_blank");
    if (win) {
      win.document.write(
        "<html><head><title>Loading Delivery Note...</title></head><body style='font-family:sans-serif;display:flex;justify-content:center;align-items:center;height:100vh;margin:0;background:#f5f7fa;'><div style='text-align:center;'><h3 style='color:#1976d2;margin-bottom:8px;'>Generating Delivery Note PDF...</h3><p style='color:#666;font-size:14px;'>Please wait a moment while your document is being fetched.</p></div></body></html>"
      );
    }
    try {
      const response = await apiData("get", `${GetDeliveryNotePdfAPI}?orderId=${orderId}`);
      const pdfUrl =
        response?.data?.pdfUrl ||
        response?.pdfUrl ||
        (typeof response?.data === "string" ? response.data : null);

      if (pdfUrl) {
        if (win && !win.closed) {
          win.location.href = pdfUrl;
        } else {
          window.open(pdfUrl, "_blank");
        }
      } else {
        if (win && !win.closed) win.close();
        alert("Could not generate Delivery Note PDF.");
      }
    } catch (err) {
      console.error("Error fetching Delivery Note PDF:", err);
      if (win && !win.closed) win.close();
      alert("Failed to fetch Delivery Note PDF.");
    }
  };

  const handleExportData = async () => {
    try {
      setLoading(true);

      const payload = {
        pageIndex: 0,
        pageSize: 10000000,
        sorting: sorting.length > 0 ? JSON.stringify(sorting[0]) : null,
        search: filters.length > 0 ? JSON.stringify(filters) : null,
        globalSearch: globalSearch || undefined,
      };

      const response = await apiData("get", fetchOrdersAPI, payload);
      const dataArray = Array.isArray(response.data.result)
        ? response.data.result
        : [];
      if (dataArray.length === 0) {
        alert("No data to export!");
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Invoices", { views: [{ state: "frozen", ySplit: 1 }] });
      const num = (v) => Math.round((parseFloat(v) || 0) * 100) / 100;

      // Define columns
      worksheet.columns = [
        { header: "#", key: "index", width: 8 },
        { header: "Invoice No.", key: "order_number", width: 20 },
        { header: "Invoice Type", key: "invoice_type", width: 16 },
        { header: "CDN Ref", key: "proforma_number", width: 20 },
        { header: "FOC", key: "foc_status", width: 12 },
        { header: "Invoice Date", key: "order_date", width: 15 },
        { header: "Entry Date", key: "created_at", width: 15 },
        { header: "Last Updated", key: "updated_at", width: 15 },
        { header: "Created By", key: "created_by_name", width: 18 },
        { header: "View Invoice", key: "order_receipt", width: 40 },
        { header: "Delivery Note", key: "delivery_note", width: 22 },
        { header: "Store", key: "store_name", width: 25 },
        { header: "Company Name / Bill To Company", key: "customer_name", width: 30 },
        { header: "Phone", key: "customer_phone", width: 20 },
        { header: "Email", key: "customer_email", width: 30 },
        { header: "Product Name", key: "product_name", width: 36 },
        { header: "SKU", key: "sku", width: 18 },
        { header: "Qty", key: "quantity", width: 10 },
        { header: "Unit Price (Excl. Tax)", key: "unit_ex", width: 16 },
        { header: "Unit Price (Incl. Tax)", key: "unit_inc", width: 16 },
        { header: "Discount %", key: "disc_pct", width: 11 },
        { header: "Discount Amount", key: "disc_amt", width: 15 },
        { header: "Taxable Value", key: "taxable", width: 15 },
        { header: "Product VAT", key: "line_vat", width: 13 },
        { header: "Line Total (Incl. Tax)", key: "line_total", width: 17 },
        { header: "Subtotal", key: "subtotal", width: 15 },
        { header: "Tax", key: "tax", width: 15 },
        { header: "Shipping", key: "shipping", width: 15 },
        { header: "Discount", key: "discount", width: 15 },
        { header: "Grand Total / Outstanding Amount", key: "grand_total", width: 25 },
        { header: "Received", key: "received", width: 20 },
        { header: "Balance Due", key: "balance_due", width: 20 },
        { header: "Payment Terms", key: "payment_type", width: 20 },
        { header: "Payment Method", key: "payment_method", width: 20 },
        { header: "Due Date", key: "due_date", width: 15 },
        { header: "Payment Status", key: "payment_status", width: 20 },
        { header: "Payment Date", key: "payment_date", width: 15 },
        {
          header: "Payment Description",
          key: "payment_description",
          width: 30,
        },
        { header: "Transaction Id", key: "transaction_id", width: 25 },
        { header: "Payment Attachment", key: "payment_attachment", width: 24 },
        { header: "Status", key: "status", width: 15 },
      ];

      const AED = '"AED" #,##0.00';
      const num2 = (v) => Math.round((parseFloat(v) || 0) * 100) / 100;

      // Add Data Rows
      dataArray.forEach((item, index) => {
        const lines = Array.isArray(item.items) ? item.items : [];
        const isFocLine = (l) => Number(l.is_foc ?? 0) === 1;

        // The list data carries no tax rate / mode, so both are worked back from
        // the saved amounts: rate = VAT ÷ (grand − VAT − shipping); the lines are
        // tax-inclusive when their total_price sum matches (grand − shipping).
        const vatTotal = num2(item.tax_amount);
        const shipTotal = num2(item.shipping_amount);
        const grandTotal = num2(item.grand_total);
        const netExcl = Math.max(0, grandTotal - vatTotal - shipTotal);
        // The saved rate / mode when the API sends them, else worked back as above.
        const taxRate =
          parseFloat(item.tax_type) ||
          (netExcl > 0 && vatTotal > 0 ? Math.round((vatTotal / netExcl) * 10000) / 100 : 0);
        const lineSum = lines.reduce((t, l) => t + (isFocLine(l) ? 0 : parseFloat(l.total_price || 0)), 0);
        const inclusive = item.tax_mode
          ? item.tax_mode === "inclusive"
          : vatTotal > 0 &&
            Math.abs(lineSum - (grandTotal - shipTotal)) <= 0.05 &&
            Math.abs(lineSum - netExcl) > 0.05;
        const exF = inclusive ? 1 / (1 + taxRate / 100) : 1;
        // A FOC line is free, but the invoice still charges VAT on its normal price.
        const unitExOf = (l) => (parseFloat(l.selling_price) || 0) * exF;

        // A discount given on the whole invoice is shared across its paid lines in
        // proportion to their value, so the lines add up to the invoice total.
        const paidEx = lines.reduce((t, l) => t + (isFocLine(l) ? 0 : (parseFloat(l.total_price) || 0) * exF), 0);
        // Some old invoices saved their VAT in the discount field too. Use the saved
        // discount only when it brings the lines closer to the invoice total.
        const rawDisc = num2(item.discount_amount);
        const target = grandTotal - shipTotal;
        const gap = (d) => Math.abs((paidEx - d) * (1 + taxRate / 100) - target);
        const docDisc = rawDisc > 0 && gap(rawDisc) <= gap(0) ? rawDisc : 0;

        const pdVals = [];
        lines.forEach((l) => {
          const qty = parseFloat(l.quantity) || 0;
          const foc = isFocLine(l);
          const ownEx = foc ? 0 : (parseFloat(l.total_price) || 0) * exF;
          const share = paidEx > 0 ? docDisc * (ownEx / paidEx) : 0;
          const disc = foc ? unitExOf(l) * qty : (parseFloat(l.discount_amount) || 0) * exF + share;
          const finalEx = ownEx - share;
          // Older FOC invoices were saved without VAT, so only charge it when the invoice has VAT.
          const vatAmt = (foc ? (vatTotal > 0 ? unitExOf(l) * qty : 0) : finalEx) * (taxRate / 100);
          const grossEx = foc ? unitExOf(l) * qty : ownEx + (parseFloat(l.discount_amount) || 0) * exF;
          pdVals.push({
            unit_ex: num2(unitExOf(l)),
            unit_inc: num2(unitExOf(l) * (1 + taxRate / 100)),
            disc_pct: grossEx > 0 ? Math.round((disc / grossEx) * 10000) / 100 : 0,
            disc_amt: num2(disc),
            taxable: num2(finalEx),
            line_vat: num2(vatAmt),
            line_total: num2(finalEx + vatAmt),
          });
        });

        const docFields = {
          index: index + 1,
          order_number: item.order_number || "",
          proforma_number: item.proforma_number || "",
          foc_status: getFocStatusLabel(item),
          order_receipt: item.order_receipt || "NA",
          invoice_type:
            item.payments && item.payments.length
              ? item.payments[0].payment_type === 1
                ? "Credit Invoice"
                : "Invoice"
              : "-",
          created_by_name: item.created_by_name || "NA",
          delivery_note: item.delivery_note_url
            ? { text: "View delivery note", hyperlink: item.delivery_note_url }
            : "",
          payment_attachment: item.payments?.[0]?.payment_attachment_url
            ? { text: "View attachment", hyperlink: item.payments[0].payment_attachment_url }
            : "",
          order_date: item.order_date
            ? moment(item.order_date).format("DD-MM-YYYY")
            : "",
          created_at: item.created_at
            ? moment(item.created_at).format("DD-MM-YYYY")
            : "",
          updated_at: item.updated_at
            ? moment(item.updated_at).format("DD-MM-YYYY")
            : "",
          store_name: item.store_name || "",
          customer_name: item.customer_name || "",
          customer_phone: item.customer_phone || "",
          customer_email: item.customer_email || "",
          subtotal: docAmounts(item).subtotal,
          discount: docAmounts(item).discount,
          tax: docAmounts(item).vat,
          shipping: docAmounts(item).shipping,
          grand_total: num2(item.grand_total && parseFloat(item.grand_total) > 0
            ? parseFloat(item.grand_total)
            : Math.max(
              0,
              (item.items?.reduce(
                (acc, i) => acc + parseFloat(i.total_price || 0),
                0,
              ) || 0) -
              ((item.items?.reduce(
                (acc, i) => acc + parseFloat(i.discount_amount || 0),
                0,
              ) || 0) +
                parseFloat(item.discount_amount || 0)),
            ) *
            1.05 +
            parseFloat(item.shipping_amount || 0)
          ),
          received: num2(Math.max(
            0,
            parseFloat(item.grand_total || 0) -
              (item.payments || []).reduce((t, p) => t + parseFloat(p.balance_due || 0), 0)
          )),
          balance_due: num2((item.payments || []).reduce((t, p) => t + parseFloat(p.balance_due || 0), 0)),
          payment_type: item.payments
            ? item.payments
              .map((p) => getPaymentTypeLabel(p.payment_type))
              .join("\n")
            : "",
          payment_method: item.payments
            ? item.payments.map((p) => p.payment_method || "").join("\n")
            : "",
          due_date: item.payments
            ? item.payments
              .map((p) =>
                p.due_date ? moment(p.due_date).format("DD-MM-YYYY") : "",
              )
              .join("\n")
            : "",
          payment_status: item.payments
            ? item.payments.map((p) => getPaymentStatusLabel(p, item.order_status)).join("\n")
            : "",
          payment_date: item.payments
            ? item.payments
              .map((p) => {
                const d = p.payment_date || p.updated_at || p.created_at;
                return d ? moment(d).format("DD-MM-YYYY") : "";
              })
              .join("\n")
            : "",
          payment_description: item.payments
            ? item.payments.map((p) => p.description || "").join("\n")
            : "",
          transaction_id: item.payments
            ? item.payments
              .map((p) => p.transaction_id || p.transaction_ref || "")
              .join("\n")
            : "",
          payment_attachment: item.payments?.[0]?.payment_attachment_url
            ? {
                text: item.payments[0].payment_attachment_name || "View attachment",
                hyperlink: item.payments[0].payment_attachment_url,
              }
            : "",
          invoice_type: item.payments?.length
            ? item.payments[0].payment_type === 1
              ? "Credit Invoice"
              : "Invoice"
            : "",
          created_by_name: item.created_by_name || "",
          delivery_note: item.delivery_note_url
            ? { text: "View delivery note", hyperlink: item.delivery_note_url }
            : "",
          status:
            item.order_status === "Completed"
              ? "Completed"
              : item.order_status === "Cancelled"
                ? "Cancelled"
                : "Pending",
        };
        // One block per invoice: document fields written once on the first
        // product row and merged down, so each invoice reads as a single row
        // with only the product columns repeating beneath it.
        const rowLines = item.items && item.items.length ? item.items : [null];
        const startRow = worksheet.rowCount + 1;
        rowLines.forEach((i, li) => {
          let product = { product_name: "", sku: "", quantity: "" };
          if (i) {
            product = {
              product_name: i.product_name || "",
              sku: i.sku || "",
              quantity: parseFloat(i.quantity) || 0,
              ...(pdVals[li] || {}),
            };
          }
          worksheet.addRow({ ...(li === 0 ? docFields : {}), ...product });
        });
        const endRow = worksheet.rowCount;
        if (endRow > startRow) {
          // Product columns 16-25 (Product Name ... Line Total) vary per row;
          // every other column is merged down the block.
          const productCols = new Set([16, 17, 18, 19, 20, 21, 22, 23, 24, 25]);
          for (let c = 1; c <= worksheet.columns.length; c += 1) {
            if (!productCols.has(c)) worksheet.mergeCells(startRow, c, endRow, c);
          }
        }
      });

      // Total footer — same figures as the list's bottom bar (all filtered rows).
      const footerRows = [
        worksheet.addRow({
          order_number: `Total (${Number(response.data.activeCount ?? 0)} ${Number(response.data.activeCount ?? 0) === 1 ? "record" : "records"}, excl. ${Number(response.data.cancelledCount ?? 0)} cancelled)`,
          grand_total: num2(response.data.totalAmount),
          received: num2(response.data.totalReceived),
          balance_due: num2(response.data.totalBalanceDue),
        }),
        ...(Number(response.data.cancelledCount) > 0
          ? [
              worksheet.addRow({
                order_number: `Cancelled (${response.data.cancelledCount}) - payment received`,
                proforma_number: num2(response.data.cancelledReceived),
              }),
            ]
          : []),
      ];
      footerRows.forEach((r) => (r.font = { bold: true }));

      // Money columns are real numbers formatted as AED.
      ["unit_ex", "unit_inc", "disc_amt", "taxable", "line_vat", "line_total", "subtotal", "tax", "shipping", "discount", "grand_total", "received", "balance_due"].forEach(
        (k) => (worksheet.getColumn(k).numFmt = AED),
      );
      worksheet.getColumn("disc_pct").numFmt = '0.00"%"';
      const cancelledRowNo = Number(response.data.cancelledCount) > 0 ? worksheet.rowCount : null;
      if (cancelledRowNo) worksheet.getCell(`C${cancelledRowNo}`).numFmt = AED;


      // Styling
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FF000000" } };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Wrap Text and Borders for all data cells
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          row.eachCell((cell) => {
            cell.alignment = {
              wrapText: true,
              vertical: "middle",
              horizontal: "left",
            };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = `Invoice_List_${moment().format("DD-MM-YYYY")}.xlsx`;
      saveAs(new Blob([buffer]), fileName);

      setSuccessMessage("Excel Downloaded Successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  // ----- Compose multiple invoices into one PDF -----
  const hasReceipt = (order) =>
    order?.order_receipt &&
    order.order_receipt !== "NA" &&
    String(order.order_receipt).trim() !== "";

  const openMergeDialog = async () => {
    setMergeMessage("");
    setMergeSelectedIds([]);
    setMergeSearch("");
    setMergeDialogOpen(true);
    await fetchOrdersForMerge();
  };

  // Reuse the same "fetch everything" pattern as the Excel export.
  const fetchOrdersForMerge = async () => {
    try {
      setMergeFetching(true);
      const payload = {
        pageIndex: 0,
        pageSize: 10000000,
        sorting: sorting.length > 0 ? JSON.stringify(sorting[0]) : null,
        search: filters.length > 0 ? JSON.stringify(filters) : null,
      };
      const response = await apiData("get", fetchOrdersAPI, payload);
      const dataArray = Array.isArray(response.data.result)
        ? response.data.result
        : [];
      setMergeOrders(dataArray);
    } catch (error) {
      console.error("Failed to load orders for merge:", error);
      setMergeOrders([]);
      setMergeMessage("Failed to load orders. Please try again.");
    } finally {
      setMergeFetching(false);
    }
  };

  const toggleMergeSelect = (orderId) => {
    setMergeSelectedIds((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId],
    );
  };

  const toggleMergeSelectAll = (selectableIds, allSelected) => {
    if (allSelected) {
      setMergeSelectedIds((prev) =>
        prev.filter((id) => !selectableIds.includes(id)),
      );
    } else {
      setMergeSelectedIds((prev) =>
        Array.from(new Set([...prev, ...selectableIds])),
      );
    }
  };

  // Orders that match the active selection mode (before the receipt check).
  const getMergeTargetOrders = () => {
    if (mergeMode === "manual") {
      return mergeOrders.filter((o) => mergeSelectedIds.includes(o.order_id));
    }

    let start;
    let end;
    if (mergeMode === "month") {
      start = dayjs(mergeFromMonth).startOf("month");
      end = dayjs(mergeToMonth).endOf("month");
    } else {
      start = dayjs(mergeFromDate).startOf("day");
      end = dayjs(mergeToDate).endOf("day");
    }
    // Tolerate a reversed range (From after To).
    if (start.isAfter(end)) {
      const tmp = start;
      start = end;
      end = tmp;
    }

    return mergeOrders.filter((o) => {
      if (!o.order_date) return false;
      const d = dayjs(o.order_date);
      return !d.isBefore(start) && !d.isAfter(end);
    });
  };

  const buildMergeFileName = () => {
    if (mergeMode === "manual") {
      return `Invoices_Selected_${moment().format("DD-MM-YYYY_HH-mm")}.pdf`;
    }
    if (mergeMode === "month") {
      const fromLabel = dayjs(mergeFromMonth).format("MM-YYYY");
      const toLabel = dayjs(mergeToMonth).format("MM-YYYY");
      // Same month on both ends: drop the redundant "_to_" range.
      return fromLabel === toLabel
        ? `Invoices_${fromLabel}.pdf`
        : `Invoices_${fromLabel}_to_${toLabel}.pdf`;
    }
    const fromLabel = dayjs(mergeFromDate).format("DD-MM-YYYY");
    const toLabel = dayjs(mergeToDate).format("DD-MM-YYYY");
    // Same date on both ends: drop the redundant "_to_" range.
    return fromLabel === toLabel
      ? `Invoices_${fromLabel}.pdf`
      : `Invoices_${fromLabel}_to_${toLabel}.pdf`;
  };

  const handleMergeDownload = async () => {
    const targets = getMergeTargetOrders();
    if (targets.length === 0) {
      setMergeMessage("No orders match your selection.");
      return;
    }

    const withReceipts = targets.filter(hasReceipt);
    if (withReceipts.length === 0) {
      setMergeMessage(
        `${targets.length} order(s) matched, but none have a receipt PDF to merge.`,
      );
      return;
    }

    try {
      setMerging(true);
      setMergeMessage("");

      const mergedPdf = await PDFDocument.create();
      let added = 0;
      let failed = 0;

      // Sort by order date so the composed PDF reads chronologically.
      const ordered = [...withReceipts].sort((a, b) =>
        dayjs(a.order_date).valueOf() - dayjs(b.order_date).valueOf(),
      );

      for (const order of ordered) {
        try {
          const res = await fetch(order.order_receipt);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const buf = await res.arrayBuffer();
          const src = await PDFDocument.load(buf, { ignoreEncryption: true });
          const pages = await mergedPdf.copyPages(src, src.getPageIndices());
          pages.forEach((p) => mergedPdf.addPage(p));
          added += 1;
        } catch (err) {
          console.error(
            `Failed to add receipt for order ${order.order_number}:`,
            err,
          );
          failed += 1;
        }
      }

      if (added === 0) {
        setMergeMessage(
          "Could not read any receipt PDFs. They may be unavailable right now.",
        );
        return;
      }

      const bytes = await mergedPdf.save();
      saveAs(
        new Blob([bytes], { type: "application/pdf" }),
        buildMergeFileName(),
      );

      const skipped = targets.length - withReceipts.length;
      setMergeDialogOpen(false);
      setSuccessMessage(
        `Merged ${added} invoice(s) into one PDF` +
        (failed ? `, ${failed} failed` : "") +
        (skipped ? `, ${skipped} without receipt skipped` : "") +
        ".",
      );
    } catch (error) {
      console.error("Merge failed:", error);
      setMergeMessage("Something went wrong while composing the PDF.");
    } finally {
      setMerging(false);
    }
  };

  // Derived values for the merge dialog UI.
  const mergeFilteredOrders = mergeOrders.filter((o) => {
    if (!mergeSearch.trim()) return true;
    const q = mergeSearch.trim().toLowerCase();
    return (
      String(o.order_number || "")
        .toLowerCase()
        .includes(q) ||
      String(o.customer_name || "")
        .toLowerCase()
        .includes(q)
    );
  });
  const mergeMatchedCount =
    mergeMode === "manual"
      ? mergeSelectedIds.length
      : getMergeTargetOrders().filter(hasReceipt).length;
  const mergeSelectableIds = mergeFilteredOrders
    .filter(hasReceipt)
    .map((o) => o.order_id);
  const mergeAllSelected =
    mergeSelectableIds.length > 0 &&
    mergeSelectableIds.every((id) => mergeSelectedIds.includes(id));

  return (
    <Layout>
      <SettlePaymentDialog
        open={settleDialogOpen}
        onClose={() => setSettleDialogOpen(false)}
        isReadOnly={isReadOnly}
        paymentId={selectedPaymentId}
        initialMethod={settleMethod}
        initialTransactionId={settleTransactionId}
        initialDescription={settleDescription}
        submitting={settlingPayment}
        orderNumber={selectedOrderRow?.order_number}
        customerName={selectedOrderRow?.customer_name}
        customerEmail={selectedOrderRow?.customer_email}
        invoiceDate={
          selectedOrderRow?.order_date
            ? dayjs(selectedOrderRow.order_date).format("DD-MM-YYYY")
            : ""
        }
        amount={
          selectedOrderRow ? `${formatAED(getOrderGrandTotal(selectedOrderRow))}` : ""
        }
        balanceDue={
          settleBalanceDue !== null
            ? settleBalanceDue
            : selectedOrderRow
              ? getOrderGrandTotal(selectedOrderRow)
              : 0
        }
        storeName={selectedOrderRow?.store_name}
        previewUrl={selectedOrderRow?.order_receipt}
        onSubmit={(paymentData) =>
          handleSubmitPayment(selectedPaymentId, paymentData)
        }
      />

      {/* Invoice conversion audit trail (Credit Invoice -> Invoice: who, when) */}
      <Dialog
        open={conversionHistoryDialogOpen}
        onClose={() => setConversionHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Invoice Conversion History</DialogTitle>
        <DialogContent dividers>
          {loadingConversionHistory ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              <CircularProgress size={24} />
            </div>
          ) : conversionHistory.length === 0 ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              No conversions found.
            </div>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>Emailed To</TableCell>
                  <TableCell>Changed By</TableCell>
                  <TableCell>Changed On</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {conversionHistory.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>Credit Invoice → Invoice</TableCell>
                    <TableCell>{row.emailed_to || "—"}</TableCell>
                    <TableCell>{row.changed_by_name || "—"}</TableCell>
                    <TableCell>
                      {dayjs(row.changed_at).format("DD-MM-YYYY")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConversionHistoryDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Date History Dialog */}
      <Dialog
        open={openHistoryDialog}
        onClose={() => setOpenHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Order Date Change History</DialogTitle>

        <DialogContent dividers>
          {loadingHistory ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              <CircularProgress size={24} />
            </div>
          ) : orderDateHistory.length === 0 ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              No date changes found.
            </div>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Previous Order Date</TableCell>
                  <TableCell>New Order Date</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Changed By</TableCell>
                  <TableCell>Changed On</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {orderDateHistory.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {dayjs(row.old_order_date).format("DD-MM-YYYY")}
                    </TableCell>
                    <TableCell>
                      {dayjs(row.new_order_date).format("DD-MM-YYYY")}
                    </TableCell>
                    <TableCell>{row.change_reason}</TableCell>
                    <TableCell>{row.changed_by_name}</TableCell>
                    <TableCell>
                      {dayjs(row.changed_at).format("DD-MM-YYYY")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenHistoryDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Full ordered-products list for one order (table rows show only a summary) */}
      <Dialog
        open={!!itemsDialogOrder}
        onClose={() => setItemsDialogOrder(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Ordered Products
          {itemsDialogOrder ? ` — ${itemsDialogOrder.order_number}` : ""}
        </DialogTitle>

        <DialogContent dividers>
          {!itemsDialogOrder || (itemsDialogOrder.items || []).length === 0 ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              No products found.
            </div>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Qty</TableCell>
                  <TableCell>Price (Per Qty)</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {itemsDialogOrder.items.map((item) => (
                  <TableRow key={item.order_item_id}>
                    <TableCell>
                      {item.product_name} ({item.quantity} {item.name})
                    </TableCell>
                    <TableCell>{formatNumber(item.quantity)}</TableCell>
                    <TableCell>{formatAED(item.selling_price)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setItemsDialogOrder(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Received breakdown — where an invoice's received amount came from */}
      <Dialog
        open={!!receivedDialog}
        onClose={() => setReceivedDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Payments Received
          {receivedDialog ? ` — ${receivedDialog.order.order_number}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {receivedDialog?.loading ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (receivedDialog?.rows || []).length === 0 ? (
            <Typography sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
              No payments recorded yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Reference No.</TableCell>
                  <TableCell>Received As</TableCell>
                  <TableCell>Recorded By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receivedDialog.rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {r.advance_payment_id && r.advance_payment_date
                        ? dayjs(r.advance_payment_date).format("DD-MM-YYYY")
                        : formatLocalDateTime(r.created_at, "DD-MM-YYYY hh:mm A")}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {formatAED(r.amount)}
                    </TableCell>
                    <TableCell>{r.payment_method || "-"}</TableCell>
                    <TableCell>{r.transaction_id || "-"}</TableCell>
                    <TableCell>
                      {r.advance_payment_id
                        ? `Advance on ${r.advance_source_number || (r.advance_source_type === "proforma_quote" ? "Proforma" : "CDN")}`
                        : r.paidAtInvoice
                          ? "Paid at invoice"
                          : "Invoice payment"}
                    </TableCell>
                    <TableCell>{r.created_by_name || "-"}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Total Received</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    AED{" "}
                    {formatAED(
                      receivedDialog.rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0)
                    )}
                  </TableCell>
                  <TableCell colSpan={4} />
                </TableRow>
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceivedDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Full payment list for one order (table rows show only a summary) */}
      <Dialog
        open={!!paymentsDialogOrder}
        onClose={() => setPaymentsDialogOrder(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: "1.1rem", pb: 1.5 }}>
          Payment Details
          {paymentsDialogOrder ? (
            <span style={{ fontWeight: 600, color: "#64748B" }}> — {paymentsDialogOrder.order_number}</span>
          ) : (
            ""
          )}
        </DialogTitle>

        <DialogContent dividers>
          {!paymentsDialogOrder ||
            (paymentsDialogOrder.payments || []).length === 0 ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              No payments found.
            </div>
          ) : (
            <Table
              size="small"
              sx={{
                border: "1px solid #E2E8F0",
                borderCollapse: "separate",
                borderRadius: "10px",
                overflow: "hidden",
                "& .MuiTableHead-root .MuiTableCell-root": {
                  backgroundColor: "#F1F5F9 !important",
                  color: "#475569 !important",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  whiteSpace: "nowrap",
                  border: "none !important",
                  borderBottom: "1px solid #E2E8F0 !important",
                  py: 1.1,
                },
                "& .MuiTableBody-root .MuiTableCell-root": {
                  whiteSpace: "nowrap",
                  fontSize: "0.85rem",
                  border: "none",
                  borderBottom: "1px solid #EEF2F7",
                  py: 1.25,
                },
                "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: "#F8FAFC" },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Payment Terms</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Payment Date</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Balance Due</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Transaction Id</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paymentsDialogOrder.payments.map((p) => {
                  const isCredit = p.payment_type === 1;
                  return (
                    <TableRow key={p.payment_id}>
                      <TableCell>{getPaymentTypeLabel(p.payment_type)}</TableCell>
                      <TableCell>{p.payment_method || "N/A"}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {p.amount != null ? `${formatAED(p.amount)}` : "-"}
                      </TableCell>
                      <TableCell>
                        {p.payment_date ? dayjs(p.payment_date).format("DD-MM-YYYY") : "N/A"}
                      </TableCell>
                      <TableCell>
                        {p.due_date
                          ? dayjs(p.due_date).format("DD-MM-YYYY")
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <span
                          style={(() => {
                            const lab = getPaymentStatusLabel(p, paymentsDialogOrder?.order_status);
                            const good = lab === "Paid";
                            const bad = lab === "Overdue";
                            return {
                              cursor: isCredit ? "pointer" : "default",
                              display: "inline-block",
                              padding: "2px 10px",
                              borderRadius: 999,
                              fontSize: 12,
                              fontWeight: 700,
                              backgroundColor: good ? "#E7F6EC" : bad ? "#FDECEC" : "#FFF4E5",
                              color: good ? "#0F5132" : bad ? "#B42318" : "#9A5B00",
                              textDecoration: isCredit ? "underline" : "none",
                            };
                          })()}
                          title={
                            !isCredit
                              ? "Payment method does not allow status update"
                              : p.payment_status === "Pending"
                                ? "Click to edit payment"
                                : "Click to view payment"
                          }
                          onClick={() => {
                            if (!isCredit) return;
                            setSelectedPaymentId(p.payment_id);
                            setSelectedOrderId(paymentsDialogOrder.order_id);
                            setSelectedOrderRow(paymentsDialogOrder);
                            setIsReadOnly(p.payment_status === "Paid");
                            loadPaymentIntoDialog(p);
                            setSettleDialogOpen(true);
                            setPaymentsDialogOrder(null);
                          }}
                        >
                          {getPaymentStatusLabel(p, paymentsDialogOrder?.order_status)}
                          {getOverdueDays(p, paymentsDialogOrder?.order_status) > 0
                            ? ` · ${overdueText(getOverdueDays(p, paymentsDialogOrder?.order_status))}`
                            : ""}
                        </span>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: Number(p.balance_due) > 0 ? "#B42318" : "#0F5132" }}>
                        {p.balance_due != null ? `${formatAED(p.balance_due)}` : "-"}
                      </TableCell>
                      <TableCell>{p.description || "N/A"}</TableCell>
                      <TableCell>
                        {p.transaction_id || p.transaction_ref || "-"}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Sum of the payment lines against the invoice total */}
                {(() => {
                  const lines = paymentsDialogOrder.payments || [];
                  const sumLines = lines.reduce((a, x) => a + (parseFloat(x.amount) || 0), 0);
                  const invoiceTotal = parseFloat(paymentsDialogOrder.grand_total);
                  return (
                    <TableRow sx={{ "& td": { fontWeight: 800, backgroundColor: "#F8FAFC", borderBottom: "none !important", fontSize: "0.9rem" } }}>
                      <TableCell colSpan={2}>Total of payment lines</TableCell>
                      <TableCell align="right">{formatAED(sumLines)}</TableCell>
                      <TableCell colSpan={6} sx={{ color: "#64748b", fontWeight: 500 }}>
                        {Number.isFinite(invoiceTotal) ? `Invoice total: ${formatAED(invoiceTotal)}` : ""}
                      </TableCell>
                    </TableRow>
                  );
                })()}
              </TableBody>
            </Table>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setPaymentsDialogOrder(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ReturnExchangeDialog
        open={!!returnOrder}
        order={returnOrder}
        onClose={() => setReturnOrder(null)}
        onDone={() => {
          setReturnOrder(null);
          setSuccessMessage("Return processed successfully.");
          fetchOrders();
        }}
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000} // hide after 3 seconds
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          sx={{
            width: "100%",
            backgroundColor: "#28a745",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              {location.state?.reportTitle || location.state?.title || "Invoice Report"}
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Invoices</Typography>
            </Breadcrumbs>
            {/* Shows which payment method this list was drilled into from on
                the dashboard (e.g. clicking "Cash" on the Monthly Sales
                card) — removable without leaving the page. */}
            {filters.some((f) => f.id === "payment_method" || f.id === "payment_method_exact") && (
              <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}>
                <Chip
                  label={`Payment Method: ${filters.find((f) => f.id === "payment_method" || f.id === "payment_method_exact")?.value}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  onDelete={() =>
                    setFilters((prev) => prev.filter((f) => f.id !== "payment_method" && f.id !== "payment_method_exact"))
                  }
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            )}
          </Grid>
          <Grid
            textAlign="right"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Tooltip
              title={
                invoiceAccess.can_create_invoice ? "" : "Access restricted"
              }
            >
              <span>
                <LoadingButton
                  size="medium"
                  variant="contained"
                  color="success"
                  className="button_first"
                  disabled={!invoiceAccess.can_create_invoice}
                  onClick={() => navigate("/invoice/add")}
                  startIcon={<AddIcon />}
                >
                  Create Invoice
                </LoadingButton>
              </span>
            </Tooltip>
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        {filters.some((x) => x.id === "real_invoices") && (
          <Alert
            severity="info"
            sx={{ mb: 1.5, borderRadius: 2 }}
            action={
              <Button
                size="small"
                onClick={() => {
                  setFilters((prev) => prev.filter((x) => x.id !== "real_invoices"));
                  setPageIndex(0);
                }}
              >
                Show all
              </Button>
            }
          >
            Showing finished invoices only, as counted on the dashboard (cancelled and unfinished invoices are hidden).
          </Alert>
        )}

        <ListFilterBar
          showProduct
          value={barValue}
          onChange={applyBar}
          onClear={() => {
            setFilters((prev) => prev.filter((x) => !BAR_FILTER_IDS.includes(x.id)));
            setGlobalSearch("");
            setPageIndex(0);
          }}
        />

        <Grid className="form_design">
          <MaterialReactTable
            columns={columns}
            data={orderList}
            enableGlobalFilter={false}
            enableFullScreenToggle={false}
            enableStickyHeader
            enableColumnPinning
            initialState={{
              columnPinning: {
                left: ["mrt-row-numbers", "order_number"],
                right: ["payment_status", "actions"],
              },
            }}
            className="mat-react-tbl"
            state={{
              // Skeletons only on the first load; a progress bar on refetches
              // (page / sort / filter) so existing rows stay visible.
              isLoading: loading && orderList.length === 0,
              showProgressBars: loading,
              pagination: { pageIndex, pageSize },
              sorting,
              columnFilters: filters,
            }}
            manualPagination
            manualSorting
            enableRowNumbers
            manualFiltering
            rowCount={totalCount}
            onPaginationChange={(updater) => {
              const paginationState = updater({
                pageIndex,
                pageSize,
              });

              const { pageIndex: newPageIndex, pageSize: newPageSize } =
                paginationState;
              setPageIndex(newPageIndex);
              setPageSize(newPageSize);
            }}
            onSortingChange={setSorting}
            onColumnFiltersChange={setFilters}
            muiTableContainerProps={{
              sx: {
                maxHeight: "calc(100vh - 240px)",
                minHeight: 450,
                overflowY: "auto",
              },
            }}
            muiTablePaginationProps={{
              rowsPerPageOptions: [100],
            }}
            muiTableBodyCellProps={{
              sx: {
                padding: "3px 8px",
                border: "1px solid #ccc",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                padding: "8px",
                border: "1px solid #ccc",
                color: "#fff !important",
                fontWeight: "bold",
                backgroundColor: "#1976d2",
                "& svg": {
                  color: "#fff !important",
                },
                "& .MuiTableSortLabel-root": {
                  color: "#fff !important",
                  "&.Mui-active": {
                    color: "#fff !important",
                    "& .MuiTableSortLabel-icon": {
                      color: "#fff !important",
                    },
                  },
                },
                "& .MuiTableSortLabel-icon": {
                  color: "#fff !important",
                },
                "& .MuiInputBase-input": {
                  color: "#fff !important",
                  textFillColor: "#fff !important",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#fff !important",
                  opacity: 0.8,
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "#fff !important",
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottomColor: "#fff !important",
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#fff !important",
                },
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              sx: {
                borderBottom: "1px solid #ccc",
                backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
              },
            })}
            muiFilterTextFieldProps={{
              sx: {
                "& .MuiInputBase-input": {
                  color: "#fff !important",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#fff !important",
                  opacity: 0.8,
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "#fff !important",
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottomColor: "#fff !important",
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#fff !important",
                },
                "& .MuiSvgIcon-root": {
                  color: "#fff !important",
                },
                "& .MuiButtonBase-root": {
                  color: "#fff !important",
                },
              },
              inputProps: {
                style: {
                  color: "#fff",
                  WebkitTextFillColor: "#fff",
                },
              },
            }}
            renderTopToolbarCustomActions={({ table }) => (
              <>
                <Box
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    p: "0.5rem",
                    justifyContent: "space-between",
                    flexGrow: 1,

                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {/* RIGHT SIDE BUTTON */}
                    <Tooltip title="Export data to excel sheet" arrow>
                      <Button
                        onClick={handleExportData}
                        style={{ background: "#1a73e829" }}
                        startIcon={<FileDownload />}
                      // startIcon={<IconTableExport />}
                      >
                        Export To Excel
                      </Button>
                    </Tooltip>
                    <Tooltip
                      title="Download multiple invoices as one merged PDF"
                      arrow
                    >
                      <Button
                        onClick={openMergeDialog}
                        style={{ background: "#1a73e829" }}
                        startIcon={<PictureAsPdfIcon />}
                      >
                        Download Invoices (PDF)
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              </>
            )}
            renderBottomToolbarCustomActions={() => (
              <ListTotalsFooter
                items={[
                  { label: "Total", sub: `${activeCount ?? totalCount} ${(activeCount ?? totalCount) === 1 ? "record" : "records"}${activeCount != null && totalCount > activeCount ? ` · ${totalCount - activeCount} cancelled excluded` : ""}`, value: `${formatAED(totalAmount)}` },
                  { label: "Received", value: `${formatAED(totalReceived)}`, tone: "green" },
                  { label: "Balance Due", value: `${formatAED(totalBalanceDue)}`, tone: "amber" },
                  ...(cancelledSummary.received > 0
                    ? [{ label: `Cancelled (${cancelledSummary.count}) received`, value: `${formatAED(cancelledSummary.received)}`, tone: "red", onClick: () => setCancelledOpen(true) }]
                    : []),
                ]}
              />
            )}
          />
        </Grid>
      </Grid>
      <CancelledReceivedDialog open={cancelledOpen} type="invoice" onClose={() => setCancelledOpen(false)} />
      {/* Compose multiple invoices into one PDF */}
      <Dialog
        open={mergeDialogOpen}
        onClose={() => !merging && setMergeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            backgroundColor: "#1976d2",
            color: "#fff",
            py: 2,
          }}
        >
          <PictureAsPdfIcon sx={{ color: "#fff" }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Download Invoices as One PDF
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.85)" }}
            >
              Merge multiple order receipts into a single file
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3, backgroundColor: "#fafbfc" }}>
          <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
            <InputLabel id="merge-mode-label">Selection Mode</InputLabel>
            <Select
              labelId="merge-mode-label"
              label="Selection Mode"
              value={mergeMode}
              onChange={(e) => {
                setMergeMode(e.target.value);
                setMergeMessage("");
              }}
              sx={{ backgroundColor: "#fff" }}
            >
              <MenuItem value="month">By Month Range</MenuItem>
              <MenuItem value="date">By Date Range</MenuItem>
              <MenuItem value="manual">Manual Selection</MenuItem>
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {mergeMode === "month" && (
              <Box sx={{ display: "flex", gap: 2 }}>
                <MobileDatePicker
                  label="From Month"
                  views={["year", "month"]}
                  openTo="month"
                  inputFormat="MMM YYYY"
                  value={mergeFromMonth}
                  maxDate={mergeToMonth}
                  onChange={(v) => v && setMergeFromMonth(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      sx={{ backgroundColor: "#fff" }}
                    />
                  )}
                />
                <MobileDatePicker
                  label="To Month"
                  views={["year", "month"]}
                  openTo="month"
                  inputFormat="MMM YYYY"
                  value={mergeToMonth}
                  minDate={mergeFromMonth}
                  onChange={(v) => v && setMergeToMonth(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      sx={{ backgroundColor: "#fff" }}
                    />
                  )}
                />
              </Box>
            )}

            {mergeMode === "date" && (
              <Box sx={{ display: "flex", gap: 2 }}>
                <MobileDatePicker
                  label="From Date"
                  inputFormat="DD-MM-YYYY"
                  value={mergeFromDate}
                  maxDate={mergeToDate}
                  onChange={(v) => v && setMergeFromDate(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      sx={{ backgroundColor: "#fff" }}
                    />
                  )}
                />
                <MobileDatePicker
                  label="To Date"
                  inputFormat="DD-MM-YYYY"
                  value={mergeToDate}
                  minDate={mergeFromDate}
                  onChange={(v) => v && setMergeToDate(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      sx={{ backgroundColor: "#fff" }}
                    />
                  )}
                />
              </Box>
            )}
          </LocalizationProvider>

          {mergeMode === "manual" && (
            <Box>
              <TextField
                fullWidth
                size="small"
                placeholder="Search invoice no. / customer"
                value={mergeSearch}
                onChange={(e) => setMergeSearch(e.target.value)}
                sx={{ mb: 1, backgroundColor: "#fff" }}
              />
              <Box
                sx={{
                  maxHeight: 300,
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  backgroundColor: "#fff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 0.5,
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "#f5f7fa",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: "text.secondary" }}
                  >
                    {mergeSelectedIds.length} selected
                  </Typography>
                  <Button
                    size="small"
                    disabled={mergeSelectableIds.length === 0}
                    onClick={() =>
                      toggleMergeSelectAll(mergeSelectableIds, mergeAllSelected)
                    }
                  >
                    {mergeAllSelected ? "Clear all" : "Select all"}
                  </Button>
                </Box>
                {mergeFetching ? (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : mergeFilteredOrders.length === 0 ? (
                  <Typography sx={{ p: 2 }} variant="body2">
                    No orders found.
                  </Typography>
                ) : (
                  mergeFilteredOrders.map((o) => {
                    const receipt = hasReceipt(o);
                    const selected = mergeSelectedIds.includes(o.order_id);
                    return (
                      <Box
                        key={o.order_id}
                        onClick={() => receipt && toggleMergeSelect(o.order_id)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          borderBottom: "1px solid #f0f0f0",
                          cursor: receipt ? "pointer" : "default",
                          backgroundColor: selected ? "#e3f2fd" : "transparent",
                          "&:hover": {
                            backgroundColor: receipt
                              ? selected
                                ? "#d7ebfb"
                                : "#f5f7fa"
                              : "transparent",
                          },
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={selected}
                          onChange={() => toggleMergeSelect(o.order_id)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={!receipt}
                        />
                        <Box sx={{ flexGrow: 1, py: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {o.order_number} — {o.customer_name || "NA"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {o.order_date
                              ? dayjs(o.order_date)
                                .format("DD-MM-YYYY")
                              : "NA"}
                            {receipt ? "" : "  •  No receipt"}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          )}

          <Box
            sx={{
              mt: 2.5,
              px: 1.5,
              py: 1.25,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "#e8f0fe",
              border: "1px solid #c5dbfa",
            }}
          >
            {mergeFetching ? (
              <CircularProgress size={16} />
            ) : (
              <PictureAsPdfIcon fontSize="small" sx={{ color: "#1976d2" }} />
            )}
            <Typography
              variant="body2"
              sx={{ color: "#0d47a1", fontWeight: 600 }}
            >
              {mergeFetching
                ? "Loading orders…"
                : `${mergeMatchedCount} invoice(s) with a receipt will be included`}
            </Typography>
          </Box>

          {mergeMessage && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {mergeMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setMergeDialogOpen(false)}
            color="inherit"
            variant="outlined"
            disabled={merging}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleMergeDownload}
            color="primary"
            variant="contained"
            loading={merging}
            disabled={merging || mergeFetching || mergeMatchedCount === 0}
            startIcon={<PictureAsPdfIcon />}
          >
            Download PDF
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Step 1 — confirm the cancellation */}
      <Dialog
        open={openDialog}
        onClose={sendingOtp ? undefined : handleCloseDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#FDECEA",
              color: "#B42318",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <WarningAmber />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>Cancel invoice</Typography>
            {selectedOrder && (
              <Typography sx={{ fontSize: 13, color: "#64748B", fontWeight: 400 }}>
                {selectedOrder.order_number}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <>
              <Box sx={{ border: "1px solid #E3E6EA", borderRadius: "10px", p: 1.75, bgcolor: "#F8FAFC", mb: 2 }}>
                {[
                  ["Customer", selectedOrder.customer_name || "-"],
                  ["Date", selectedOrder.order_date ? dayjs(selectedOrder.order_date).format("DD-MM-YYYY") : "-"],
                  ["Invoice total", `${formatAED(selectedOrder.grand_total)}`],
                ].map(([k, v]) => (
                  <Box key={k} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.3 }}>
                    <Typography sx={{ fontSize: 13, color: "#64748B" }}>{k}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, textAlign: "right" }}>{v}</Typography>
                  </Box>
                ))}
              </Box>

              <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 0.75 }}>What happens</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25, color: "#475569", fontSize: 13.5, lineHeight: 1.7 }}>
                <li>The invoice is marked <b>Cancelled</b> and no longer counts in sales totals.</li>
                <li>The items go back into stock.</li>
                <li>An OTP is emailed to Admin — you enter it to confirm.</li>
                <li><b>This can't be undone.</b></li>
              </Box>

              {otpActive && otpSession.order_id === selectedOrder.order_id && (
                <Alert
                  severity="info"
                  sx={{ mt: 2, borderRadius: "10px", alignItems: "center" }}
                  action={
                    <Button size="small" color="inherit" onClick={reopenOtpDialog} sx={{ fontWeight: 700, textTransform: "none" }}>
                      Enter OTP
                    </Button>
                  }
                >
                  An OTP was already sent (<OtpTime expiresAt={otpSession.expiresAt} /> left).
                </Alert>
              )}
              {cancelError && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: "10px" }}>
                  {cancelError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={handleCloseDialog} color="inherit" variant="outlined" disabled={sendingOtp} sx={{ textTransform: "none", borderRadius: "8px" }}>
            Keep invoice
          </Button>
          <LoadingButton
            onClick={() => handleSendOtp(selectedOrder)}
            color="error"
            variant="contained"
            loading={sendingOtp}
            disabled={sendingOtp}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px" }}
          >
            {otpActive && selectedOrder && otpSession.order_id === selectedOrder.order_id
              ? "Send a new OTP"
              : "Send OTP to Admin"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Step 2 — enter the OTP. Closing it keeps the session: the reminder
          below brings it back without sending another OTP. */}
      <Dialog
        open={otpDialogOpen}
        onClose={() => setOtpDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, pb: 0.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#EEF2FF",
              color: "#2F5FED",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <LockOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>Verify cancellation</Typography>
            <Typography sx={{ fontSize: 13, color: "#64748B", fontWeight: 400 }}>
              Invoice {otpSession?.order_number}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setOtpDialogOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5, color: "#475569", mt: 1, textAlign: "center" }}>
            Enter the 4-digit OTP that was emailed to Admin.
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, mt: 2.5 }}>
            {Array.from({ length: 4 }).map((_, index) => (
              <TextField
                key={index}
                inputRef={(el) => (inputRefs.current[index] = el)}
                autoFocus={index === 0}
                error={Boolean(otpError)}
                inputProps={{
                  maxLength: 1,
                  inputMode: "numeric",
                  "aria-label": `OTP digit ${index + 1}`,
                  style: { textAlign: "center", fontSize: "1.6rem", fontWeight: 700, width: "3rem", height: "3rem", padding: 0 },
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#fff" } }}
                value={otp[index] || ""}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  const next = otp.padEnd(4, " ").split("");
                  next[index] = val ? val.slice(-1) : " ";
                  setOtp(next.join("").replace(/ +$/, ""));
                  setOtpError("");
                  if (val && index < 3) inputRefs.current[index + 1]?.focus();
                }}
                onPaste={(e) => {
                  const digits = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 4);
                  if (!digits) return;
                  e.preventDefault();
                  setOtp(digits);
                  setOtpError("");
                  inputRefs.current[Math.min(digits.length, 3)]?.focus();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSubmitOtp();
                  } else if (e.key === "Backspace") {
                    e.preventDefault();
                    const next = otp.padEnd(4, " ").split("");
                    if (next[index] && next[index] !== " ") {
                      next[index] = " ";
                    } else if (index > 0) {
                      next[index - 1] = " ";
                      inputRefs.current[index - 1]?.focus();
                    }
                    setOtp(next.join("").replace(/ +$/, ""));
                  }
                }}
              />
            ))}
          </Box>

          {otpError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: "10px" }}>
              {otpError}
            </Alert>
          )}

          {otpSession && <OtpTimerBlock expiresAt={otpSession.expiresAt} onExpiredChange={setOtpExpired} />}

          <Box sx={{ textAlign: "center", mt: 1.5 }}>
            <Button
              onClick={handleResendOtp}
              disabled={sendingOtp}
              size="small"
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {sendingOtp ? "Sending…" : !otpExpired ? "Didn't get it? Send a new OTP" : "Send a new OTP"}
            </Button>
          </Box>
          <Typography sx={{ fontSize: 12, color: "#94A3B8", textAlign: "center", mt: 0.5 }}>
            You can close this and come back — a reminder stays at the bottom of the screen while the OTP is valid.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setOtpDialogOpen(false)} color="inherit" variant="outlined" sx={{ textTransform: "none", borderRadius: "8px" }}>
            Close
          </Button>
          <LoadingButton
            onClick={handleSubmitOtp}
            color="error"
            variant="contained"
            loading={verifyingOtp}
            disabled={verifyingOtp || otp.length !== 4 || otpExpired}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px" }}
          >
            Verify &amp; cancel invoice
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Reminder while an OTP is pending and the dialog is closed */}
      {otpSession && !otpDialogOpen && !openDialog && (
        <OtpReminderBar session={otpSession} onOpen={reopenOtpDialog} onForget={clearOtpSession} />
      )}
    </Layout>
  );
}

export default OrderMasterList;
